import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type CSSProperties,
  type KeyboardEvent,
  type MutableRefObject,
  type ReactNode,
  type RefObject,
} from "react";
import { flushSync } from "react-dom";
import {
  Sparkles,
  Activity,
  TriangleAlert,
  Zap,
  ChevronLeft,
  Compass,
  X,
  Plus,
  History,
  ArrowLeft,
  Search,
  MessageSquare,
  PanelRight,
  Maximize2,
  Target,
  Trash2,
} from "lucide-react";
import type {
  PlayerAIChatMessage,
  PlayerAIContext,
  PlayerAIHoshinPayload,
  PlayerAIHoshinDecisionRow,
  PlayerAIHoshinGapRow,
  PlayerAIHoshinItemRow,
  PlayerAIMessageFeedback,
  PlayerAIMessageFeedbackRequest,
  PlayerAIMessageReference,
  PlayerAIReplyResolver,
  PlayerAIReplyResult,
  PlayerAISuggestedPrompt,
  PlayerAITabId,
  PlayerAIThread,
  PlayerAIWidgetActionHandler,
} from "./playerai.types";
import { hasModalLayer, ModalShell } from "../primitives/index.js";
import { MOTION_DURATION_MS } from "../motion.js";
import { hasOpenOverlayLayer } from "../primitives/layer-stack.js";
import { useOverlayPresence } from "../primitives/OverlaySurface.js";
import { PLAYER_AI_FLOATING_PLACEMENT } from "./playerai-floating-placement.js";
import { Skeleton } from "../atoms/Skeleton/index.js";
import { IconTile } from "../atoms/IconTile/index.js";
import { Tooltip } from "../molecules/Tooltip/index.js";
import { ResizeHandle } from "../molecules/ResizeHandle/index.js";
import { SELECTED } from "../lib/surfaces.js";
import { relativeTime } from "./relative-time.js";
import { MessageBubble } from "./MessageBubble.js";
import {
  PlayerAIComposer,
  type ComposerMentionOption,
  type ComposerMentions,
  type ComposerPastedContext,
} from "./PlayerAIComposer.js";
import {
  CHAT_ROW_ATTRIBUTE,
  CHAT_ROW_SEQUENCE_ATTRIBUTE,
  useChatScrollFollow,
  type ChatScrollFollow,
} from "./use-chat-scroll-follow.js";
import { ChatJumpControl } from "./ChatJumpControl.js";
import { CollapsibleProposal } from "./CollapsibleProposal.js";
import {
  latestProposalRowId,
  proposalNeedsAttention,
} from "./proposal-collapse.js";
import { foldHiddenExchanges, hiddenEntryHolding } from "./exchange-filter.js";
import { HiddenExchangeRow } from "./HiddenExchangeRow.js";
import {
  MentionNavigator,
  type PlayerAIMentionNavigation,
} from "./MentionNavigator.js";
import {
  ComposerApprovalNotice,
  type PlayerAIApprovalNotice,
  type PlayerAIPauseNotice,
  type PlayerAIRunControl,
} from "./ComposerRunControls.js";
import {
  PLAYER_AI_MENTION_TOKEN,
  mentionCandidateMatches,
  mentionOccurrenceIndices,
  reconcileSelectedMentionRanges,
  selectedPersonMentions,
  type SelectedMention,
} from "./composer-mentions.js";

export type {
  PlayerAIChatMessage,
  PlayerAIContext,
  PlayerAIDocumentGroundingPayload,
  PlayerAIHoshinPayload,
  PlayerAIHoshinDecisionRow,
  PlayerAIHoshinGapRow,
  PlayerAIHoshinItemRow,
  PlayerAIMessageRole,
  PlayerAIMessageFeedback,
  PlayerAIMessageFeedbackRating,
  PlayerAIMessageFeedbackRequest,
  PlayerAIReplyResolver,
  PlayerAIReplyResult,
  PlayerAIRunActivityKind,
  PlayerAIRunActivityPayload,
  PlayerAIRunActivityRow,
  PlayerAIRunActivityStatus,
  PlayerAISuggestedPrompt,
  PlayerAITabId,
  PlayerAIUnresolvedSources,
  PlayerAIWidgetPayload,
  PlayerAIWidgetTone,
  PlayerAIKpiListPayload,
  PlayerAIKpiRow,
  PlayerAIQuickReplyButton,
  PlayerAIQuickReplyPayload,
  PlayerAITaskListPayload,
  PlayerAITaskRow,
  PlayerAITaskPriority,
  PlayerAIToolProposalPayload,
  PlayerAIToolProposalStatus,
  PlayerAISignalPayload,
  PlayerAIWidgetActionHandler,
  SystemNotice,
} from "./playerai.types";

import {
  PASTED_CONTEXT_THRESHOLD,
  composeMessageWithPastedContext,
  type PastedContextItem,
} from "./PastedContext.js";

export type PlayerAIMode = "popover" | "rail" | "fullscreen";

/** What a controlled send carries beyond its text. */
export interface PlayerAISendOptions {
  readonly mentionsPlayerAI?: boolean;
  readonly playerAIMentionOccurrenceIndices?: readonly number[];
  /** Task 6 — people selected from the mention picker. */
  readonly personMentions?: ReadonlyArray<{
    readonly userId: string;
    readonly token: string;
    readonly occurrenceIndices: readonly number[];
  }>;
  /** Task 7 — the Player AI response this message replies to. */
  readonly replyToMessageId?: string;
  readonly command?: "next_phase";
}

export interface PlayerAIDraftRequest {
  readonly id: string;
  readonly text: string;
}

export interface PlayerAIRailProps {
  variant?: "expanded" | "compact";
  /** Controlled presence in every mode. Keep mounted until onExited for exit. */
  open?: boolean | undefined;
  onExited?: (() => void) | undefined;
  onToggle?: (() => void) | undefined;
  /**
   * What the user is looking at. Not rendered — it keys thread storage
   * (`scope`), seeds thread titles ("Chat in SOLVE"), and resets suggestions
   * when the user navigates elsewhere.
   */
  context?: PlayerAIContext | undefined;
  /** Suggested prompt chips shown when the thread is empty or just after a context change. */
  suggestedPrompts?: PlayerAISuggestedPrompt[] | undefined;
  /** Initial seeded chat thread. */
  initialThread?: PlayerAIChatMessage[] | undefined;
  /** Backend-owned chat timeline. When supplied, the rail renders in controlled mode. */
  messages?: PlayerAIChatMessage[] | undefined;
  /** Backend-owned chat thread history for controlled mode. */
  threads?: PlayerAIThread[] | undefined;
  /** Backend-owned active thread id for controlled mode. */
  activeThreadId?: string | null | undefined;
  /** Backend-owned thinking state for controlled mode. */
  isThinking?: boolean | undefined;
  /**
   * Backend-owned Run lifecycle for controlled mode: true from the moment
   * Player AI starts working until the Run ends, through the pauses
   * (`waiting_approval`, `waiting_documents`), the activity timeline and the
   * streamed answer that `isThinking` deliberately hides. `isThinking` is
   * presentation; this is the lifecycle, and only this may be read as
   * completion.
   */
  isRunActive?: boolean | undefined;
  /** Backend send handler for controlled mode. */
  onSendMessage?:
    | ((text: string, options?: PlayerAISendOptions) => void | Promise<void>)
    | undefined;
  /** Disable the controlled composer while a Run is active or a file uploads. */
  composerDisabled?: boolean | undefined;
  /**
   * The composer is READ-ONLY: the draft stays on screen, focused, selectable
   * and copyable, and only sending is refused.
   *
   * Deliberately not another `disabled` case. A `disabled` textarea cannot hold
   * focus, so the browser moves focus to `<body>` the moment it is set and
   * subsequent keystrokes go nowhere — which is exactly what a routine
   * permission check used to do mid-sentence (Task 7). `readOnly` keeps the
   * element focusable and the caret where it was, which is what a person who
   * has just lost access needs in order to copy what they had written.
   */
  composerReadOnly?: boolean | undefined;
  /** Context-sensitive placeholder for disabled or read-only conversations. */
  composerPlaceholder?: string | undefined;
  /**
   * The backend's own character ceiling for one message, supplied by the host
   * that owns the API contract. Present, a paste that would breach it is
   * REFUSED with the number said out loud and the draft left intact — the rail
   * never trims a paste to fit, because a silently shortened source is worse
   * than a message that did not send.
   */
  composerMaxLength?: number | undefined;
  /** Show a lightweight placeholder while the initial conversation page loads. */
  showConversationSkeleton?: boolean | undefined;
  /**
   * Host-owned transient status placed above the composer input. It sits in
   * the composer's one scroll region, which shrinks and scrolls when the
   * surface is short, so it can never push Send out.
   */
  composerStatusSlot?: ReactNode | undefined;
  /**
   * Host-owned conversation metadata, such as file attachment cards, above the
   * composer input — in the same scroll region as `composerStatusSlot`.
   */
  composerMetadataSlot?: ReactNode | undefined;
  composerContextAttachments?:
    | readonly { id: string; name: string }[]
    | undefined;
  onRemoveContextAttachment?: ((id: string) => void) | undefined;
  composerContextNotice?: string | undefined;
  /**
   * Host-owned leading composer control, such as a data-blind file picker
   * trigger. It renders in ONE 28×28 cell before the text box, so pass one icon
   * control — a text label does not fit.
   */
  composerLeadingSlot?: ReactNode | undefined;
  /**
   * Enables the inline Player AI mention suggestion in an authorized group
   * SOLVE composer. Only selecting the suggestion produces structured mention
   * metadata; typed or pasted lookalike text remains inert.
   */
  playerAIMention?:
    | {
        readonly available: boolean;
        readonly disabled?: boolean | undefined;
      }
    | undefined;
  /** Enables the SOLVE `/next_phase` command suggestion. */
  nextPhaseCommand?:
    | {
        readonly available: boolean;
        readonly disabled?: boolean | undefined;
      }
    | undefined;
  /**
   * Task 6 — the people this conversation's participants can address, offered
   * by the mention picker beside Player AI. The host loads the complete
   * authorized roster on first use; the rail filters names locally as the
   * user types. Selecting one produces a structured person mention; typed
   * `@Name` text stays inert.
   */
  personMentions?: PlayerAIPersonMentionSource | undefined;
  /** Task 6 — "N mentions ↑ ↓" at the start of the header row. */
  mentionNavigation?: PlayerAIMentionNavigation | undefined;
  /**
   * Take the reader to a message, after the host made it available (loading
   * older history if needed). A new `id` is a new request. `focus` (PB-5447
   * Review) also moves keyboard focus onto a revealed proposal card.
   */
  focusRequest?:
    | {
        readonly id: string;
        readonly messageId: string;
        readonly focus?: boolean;
      }
    | undefined;
  /**
   * Task 7 — a preview was activated whose message is not in the loaded
   * history. The host loads it and answers with a `focusRequest`.
   */
  onRequestMessage?:
    | ((reference: PlayerAIMessageReference) => void)
    | undefined;
  /** Task 14 — Stop beside the composer while a Run is active. */
  runControl?: PlayerAIRunControl | undefined;
  /** Task 14 — the shared "Player AI stopped" status, group conversations. */
  pauseNotice?: PlayerAIPauseNotice | undefined;
  /** PB-5447 — a pending approval this viewer decides; the host owns who. */
  approvalNotice?: PlayerAIApprovalNotice | undefined;
  /**
   * Task 15 — first names of OTHER participants typing now (group only).
   * Supplying it (even empty) reserves the typing line's height.
   */
  typingNames?: readonly string[] | undefined;
  /** Task 15 — the local draft started or stopped being typed. */
  onComposerActivity?: ((activity: "typing" | "idle") => void) | undefined;
  /** Task 17 — personal "My exchanges only" display filter (group only). */
  myExchangesOnly?: boolean | undefined;
  /** Uses minimal shared-thread attribution without changing individual chat. */
  groupConversation?: boolean | undefined;
  /** Host-owned controls placed below the composer, such as a workspace Next action. */
  composerFooterSlot?: ReactNode | undefined;
  /** Host-owned transient text that replaces the keyboard hint below the composer. */
  composerHintSlot?: ReactNode | undefined;
  /** Host-owned controls placed in the Player AI header. */
  headerActionsSlot?: ReactNode | undefined;
  /** Create a new backend thread in controlled mode. */
  onNewThread?: (() => void | Promise<void>) | undefined;
  /** Select a backend thread in controlled mode. */
  onSelectThread?:
    | ((thread: PlayerAIThread) => void | Promise<void>)
    | undefined;
  /** Delete a backend thread in controlled mode. */
  onDeleteThread?: ((threadId: string) => void | Promise<void>) | undefined;
  /** Load the next older backend history page when the user reaches the top. */
  onLoadOlderHistory?: (() => void | Promise<void>) | undefined;
  /** Handles inline widget actions such as approving or dismissing tool proposals. */
  onWidgetAction?: PlayerAIWidgetActionHandler | undefined;
  /** Persists feedback for an eligible backend-owned assistant response. */
  onMessageFeedback?:
    | ((
        feedback: PlayerAIMessageFeedbackRequest,
      ) => Promise<PlayerAIMessageFeedback>)
    | undefined;
  /** True when older backend history exists. */
  hasMoreHistory?: boolean | undefined;
  /** True while an older backend history page is loading. */
  isLoadingHistory?: boolean | undefined;
  /** Resolver that returns a scripted reply for a prompt + context. */
  resolveReply?: PlayerAIReplyResolver | undefined;
  /** Imperative draft seed, used by widget-level Ask PlayerAI actions. */
  draftRequest?: PlayerAIDraftRequest | undefined;
  /** Initial active tab when expanded. */
  initialTab?: PlayerAITabId | undefined;
  /**
   * Presentation mode for the expanded rail. `"rail"` (default) renders the
   * classic right-side resizable pane; `"popover"` renders a small floating
   * card anchored to the bottom-right launcher; `"fullscreen"` renders a
   * centered modal with the conversation list pinned on the left.
   */
  mode?: PlayerAIMode | undefined;
  /** Optional initial focus for non-modal popover and rail presentations. */
  initialFocus?: "none" | "composer" | undefined;
  /** Called when the user picks a different presentation mode from the header. */
  onSetMode?: ((mode: PlayerAIMode) => void) | undefined;
  /**
   * Optional Hoshin Monitor payload. When provided, the Hoshin tab renders
   * a structured snapshot of the current strategy context (open decisions,
   * AI-detected gaps, period, selected X-Matrix item) instead of the
   * placeholder.
   */
  hoshinPayload?: PlayerAIHoshinPayload | undefined;
}

let _msgId = 0;
const nextId = () => `m${Date.now().toString(36)}_${++_msgId}`;

function ctxKey(c?: PlayerAIContext) {
  if (!c) return "";
  return `${c.label}::${c.sublabel ?? ""}`;
}

const RAIL_WIDTH_KEY = "playerai.rail.width";
/** Task 8 — the side pane opens at 500px when the window allows it. */
export const PLAYER_AI_RAIL_DEFAULT_WIDTH = 500;
/** The floor; it wins whenever the percentage cap would fall below it. */
export const PLAYER_AI_RAIL_MIN_WIDTH = 280;
/** Task 8 — at most 45% of the window, with no fixed pixel cap. */
export const PLAYER_AI_RAIL_MAX_VIEWPORT_FRACTION = 0.45;
const THREADS_KEY_PREFIX = "playerai.threads.";

function sanitizeScope(label?: string) {
  const base = (label ?? "default").toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return base.replace(/^-+|-+$/g, "") || "default";
}

function deriveThreadTitle(
  messages: PlayerAIChatMessage[],
  context?: PlayerAIContext,
): string {
  const firstUser = messages.find((m) => m.role === "user");
  if (firstUser?.content) {
    const t = firstUser.content.trim();
    return t.length > 60 ? `${t.slice(0, 57)}…` : t;
  }
  return context?.label ? `Chat in ${context.label}` : "Chat";
}

function newThreadId() {
  return `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

interface SampleSpec {
  idSuffix: string;
  ageMs: number;
  messages: Array<{ role: "user" | "assistant"; content: string }>;
}

const HOME_SAMPLES: SampleSpec[] = [
  {
    idSuffix: "kpi-trends",
    ageMs: 2 * HOUR,
    messages: [
      { role: "user", content: "Which KPIs are trending down this week?" },
      {
        role: "assistant",
        content:
          "Three KPIs slipped this week. Door Sub-Line FPY is the largest mover and the only one in the red — I'd start there.",
      },
      { role: "user", content: "Pull the related A3 for that one." },
      {
        role: "assistant",
        content:
          "Opened SLV-2391 in SOLVE — it's currently in Containment with the next checkpoint due Tue.",
      },
    ],
  },
  {
    idSuffix: "today-priorities",
    ageMs: DAY + 3 * HOUR,
    messages: [
      { role: "user", content: "What needs my attention today?" },
      {
        role: "assistant",
        content:
          "Three items on your plate: approve containment for SLV-2391 (P1, due today), review the OEE drop on Robot 14, and sign off Q1 catchball for Maintenance.",
      },
    ],
  },
  {
    idSuffix: "overnight-summary",
    ageMs: 3 * DAY,
    messages: [
      { role: "user", content: "Summarize what happened overnight." },
      {
        role: "assistant",
        content:
          "Throughput stayed healthy, OEE on Line 3 dropped 4.1pp, and SLV-2391 just moved to Improve. Pattern matches the FY24 Q2 incident at Plant 04.",
      },
      { role: "user", content: "Draft a catchball ask for Maintenance." },
      {
        role: "assistant",
        content:
          "Drafted a 15-min huddle ask with Maintenance and Quality to review the Door Sub-Line FPY pattern from the last 3 shifts.",
      },
    ],
  },
];

const GENERIC_SAMPLES: SampleSpec[] = [
  {
    idSuffix: "summary",
    ageMs: 4 * HOUR,
    messages: [
      { role: "user", content: "Give me a quick summary of this view." },
      {
        role: "assistant",
        content:
          "Most signals are healthy. The two I'd watch: a recent owner change on the top-priority item and one overdue checkpoint that just slipped past its target.",
      },
    ],
  },
  {
    idSuffix: "changes",
    ageMs: DAY + 6 * HOUR,
    messages: [
      { role: "user", content: "What changed since yesterday?" },
      {
        role: "assistant",
        content:
          "Two updates: a status moved from Watch to Healthy, and a new comment landed on the top action requesting a date push.",
      },
    ],
  },
  {
    idSuffix: "next-steps",
    ageMs: 4 * DAY,
    messages: [
      { role: "user", content: "Draft next steps for the highest-risk item." },
      {
        role: "assistant",
        content:
          "Drafted a next step: schedule a 15-min review with the owner and the upstream team to align on root cause before the next checkpoint.",
      },
    ],
  },
];

function buildSampleThreads(
  scope: string,
  context: PlayerAIContext | undefined,
  now: number,
): PlayerAIThread[] {
  const specs = scope === "home" ? HOME_SAMPLES : GENERIC_SAMPLES;
  return specs.map((spec) => {
    const messages: PlayerAIChatMessage[] = spec.messages.map((m, i) => ({
      id: `sample_${scope}_${spec.idSuffix}_${i}`,
      role: m.role,
      content: m.content,
    }));
    return {
      id: `sample_${scope}_${spec.idSuffix}`,
      title: deriveThreadTitle(messages, context),
      messages,
      scope,
      updatedAt: now - spec.ageMs,
    };
  });
}

/**
 * Task 8 — clamp a width into the side pane's allowed range for a window:
 * at least 280px, at most 45% of the window, and the floor wins when 45% would
 * fall below it. Applied to the remembered width at render time, so a smaller
 * window narrows the pane without overwriting what the user chose.
 */
export function clampPlayerAIRailWidth(width: number, viewport: number) {
  const max = Math.floor(viewport * PLAYER_AI_RAIL_MAX_VIEWPORT_FRACTION);
  const safeMax = Math.max(PLAYER_AI_RAIL_MIN_WIDTH, max);
  return Math.min(Math.max(width, PLAYER_AI_RAIL_MIN_WIDTH), safeMax);
}

/**
 * Task 6 — the complete authorized member roster for this conversation.
 *
 * `options` exclude the viewer and anyone without access. The rail filters
 * these names locally against the current `@` query. Loading and failed
 * roster reads are explicit; an incomplete list is never offered as complete.
 */
export interface PlayerAIPersonMentionSource {
  readonly options: ReadonlyArray<{
    readonly id: string;
    readonly label: string;
  }>;
  /** What follows `@` now, or null when no mention is being typed. */
  readonly onQueryChange: (query: string | null) => void;
  readonly status?: "loading" | "error" | "ready" | undefined;
  readonly onRetry?: (() => void) | undefined;
}

const NO_PEOPLE: ReadonlyArray<{
  readonly id: string;
  readonly label: string;
}> = [];

/**
 * The mention picker's options for what was typed after `@`: Player AI (when
 * this viewer may mention it) and the people in the conversation.
 */
function mentionOptionsFor(
  query: string,
  playerAI: boolean,
  people: ReadonlyArray<{ readonly id: string; readonly label: string }>,
): ComposerMentionOption[] {
  const needle = query.toLowerCase();
  return [
    ...(playerAI && "player ai".startsWith(needle)
      ? [{ id: "player-ai", label: "Player AI", kind: "player_ai" as const }]
      : []),
    ...people
      .filter((person) => mentionCandidateMatches(person.label, query))
      .map((person) => ({
        id: person.id,
        label: person.label,
        kind: "person" as const,
      })),
  ];
}

function currentViewportWidth(): number {
  return typeof window === "undefined"
    ? PLAYER_AI_RAIL_DEFAULT_WIDTH / PLAYER_AI_RAIL_MAX_VIEWPORT_FRACTION
    : window.innerWidth;
}

function rememberedRailWidth(): number {
  if (typeof window === "undefined") return PLAYER_AI_RAIL_DEFAULT_WIDTH;
  try {
    const raw = window.localStorage.getItem(RAIL_WIDTH_KEY);
    const parsed = raw ? parseInt(raw, 10) : Number.NaN;
    return Number.isNaN(parsed) ? PLAYER_AI_RAIL_DEFAULT_WIDTH : parsed;
  } catch {
    return PLAYER_AI_RAIL_DEFAULT_WIDTH;
  }
}

export function PlayerAIRail({
  variant = "compact",
  open = true,
  onExited,
  onToggle,
  context,
  suggestedPrompts = [],
  initialThread = [],
  messages: controlledMessages,
  threads: controlledThreads,
  activeThreadId: controlledActiveThreadId,
  isThinking: controlledThinking,
  isRunActive,
  onSendMessage,
  composerDisabled = false,
  composerReadOnly = false,
  composerPlaceholder,
  composerMaxLength,
  showConversationSkeleton = false,
  composerStatusSlot,
  composerMetadataSlot,
  composerContextAttachments,
  onRemoveContextAttachment,
  composerContextNotice,
  composerLeadingSlot,
  playerAIMention,
  nextPhaseCommand,
  groupConversation = false,
  composerFooterSlot,
  composerHintSlot,
  headerActionsSlot,
  onNewThread: onNewControlledThread,
  onSelectThread: onSelectControlledThread,
  onDeleteThread,
  onLoadOlderHistory,
  onWidgetAction,
  onMessageFeedback,
  hasMoreHistory = false,
  isLoadingHistory = false,
  resolveReply,
  draftRequest,
  initialTab = "chat",
  mode = "rail",
  initialFocus = "none",
  onSetMode,
  hoshinPayload,
  personMentions,
  mentionNavigation,
  focusRequest,
  onRequestMessage,
  runControl,
  pauseNotice,
  approvalNotice,
  typingNames,
  onComposerActivity,
  myExchangesOnly = false,
}: PlayerAIRailProps) {
  const [tab, setTab] = useState<PlayerAITabId>(initialTab);
  const [thread, setThread] = useState<PlayerAIChatMessage[]>(initialThread);
  // Suggestions stay visible until the user sends their first message,
  // regardless of whether the thread was seeded.
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [thinking, setThinking] = useState(false);
  const [draft, setDraft] = useState("");
  // Large pastes captured out of the composer, oldest first. A second paste
  // adds a second pill; nothing ever replaces what is already attached.
  const [pastedContext, setPastedContext] = useState<PastedContextItem[]>([]);
  const [editingPastedContextId, setEditingPastedContextId] = useState<
    string | null
  >(null);
  const [selectedMentions, setSelectedMentions] = useState<SelectedMention[]>(
    [],
  );
  const [mentionRange, setMentionRange] = useState<{
    start: number;
    end: number;
  } | null>(null);
  const [activeMentionIndex, setActiveMentionIndex] = useState(0);
  const [slashRange, setSlashRange] = useState<{
    start: number;
    end: number;
  } | null>(null);
  const [hasSelectedNextPhaseCommand, setHasSelectedNextPhaseCommand] =
    useState(false);
  const playerAIMentionEnabled = Boolean(
    playerAIMention?.available && !playerAIMention.disabled,
  );
  // Task 6 — people are offered wherever a group composer can post at all;
  // mentioning a person starts nothing, so it needs no Player AI grant.
  const personMentionsEnabled =
    groupConversation && !composerDisabled && personMentions !== undefined;
  const personMentionOptions = personMentionsEnabled
    ? (personMentions?.options ?? NO_PEOPLE)
    : NO_PEOPLE;
  const onPersonMentionQuery = personMentionsEnabled
    ? personMentions?.onQueryChange
    : undefined;
  const mentionPickerEnabled = playerAIMentionEnabled || personMentionsEnabled;
  const nextPhaseCommandEnabled = Boolean(
    nextPhaseCommand?.available && !nextPhaseCommand.disabled,
  );
  // Task 8 — the REMEMBERED width is a preference; the rendered width is that
  // preference clamped to the current window. A narrower window (or a resize)
  // never overwrites what the user chose.
  const [preferredWidth, setPreferredWidth] =
    useState<number>(rememberedRailWidth);
  const [viewportWidth, setViewportWidth] =
    useState<number>(currentViewportWidth);
  const width = clampPlayerAIRailWidth(preferredWidth, viewportWidth);
  // Task 7 — the Player AI response the next message replies to. Keyed by the
  // conversation it was chosen in, so it can never follow the reader into
  // another thread.
  const [replyTarget, setReplyTarget] = useState<{
    readonly messageId: string;
    readonly text: string;
    readonly threadId: string | null | undefined;
  } | null>(null);
  const [threads, setThreads] = useState<PlayerAIThread[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string>(() =>
    newThreadId(),
  );
  const [historyOpen, setHistoryOpen] = useState(false);
  const controlledMode = controlledMessages !== undefined;
  const displayedThread = controlledMessages ?? thread;
  const displayedThinking = controlledThinking ?? thinking;
  const historyThreads = controlledThreads ?? threads;
  const displayedActiveThreadId = controlledActiveThreadId ?? activeThreadId;
  const backendHistoryMode = controlledThreads !== undefined;
  const canUseHistory = !controlledMode || backendHistoryMode;
  const compactTabRef = useRef<PlayerAITabId | null>(null);
  const prevVariantRef = useRef(variant);

  // A "pristine" thread is one where the user hasn't typed anything yet
  // (it may contain only seed/system content). Pristine threads are not
  // persisted to history.
  const pristineRef = useRef(true);

  // Tracks which scope's threads are currently in `threads`, to avoid
  // overwriting another scope's storage during a hydration race.
  const hydratedScopeRef = useRef<string | null>(null);

  // Monotonic token that invalidates pending assistant replies. Incremented
  // whenever the user sends a new prompt, starts a new thread, restores a
  // thread from history, or the scope changes — so an in-flight setTimeout
  // from an old context can't leak into a fresh thread.
  const replyTokenRef = useRef(0);
  const controlledSendPendingRef = useRef(false);
  const pendingComposerCursorRef = useRef<number | null>(null);

  const key = ctxKey(context);
  const prevKey = useRef(key);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // PB-5447 — the focus request whose keyboard focus already moved. Held here
  // because the chat body remounts (History, mode switches) and must not move
  // focus to the same card again each time.
  const focusedRequestRef = useRef<string | null>(null);
  const threadContentRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const scope = useMemo(() => {
    const a = sanitizeScope(context?.label);
    const b = sanitizeScope(context?.sublabel);
    return b === "default" ? a : `${a}--${b}`;
  }, [context?.label, context?.sublabel]);

  useEffect(() => {
    const previous = prevVariantRef.current;
    prevVariantRef.current = variant;
    if (previous === variant || variant !== "expanded") return;
    const requestedTab = compactTabRef.current ?? initialTab;
    compactTabRef.current = null;
    setTab(requestedTab);
    if (requestedTab === "chat") setHistoryOpen(false);
  }, [variant, initialTab]);

  // Re-apply the width bounds whenever the window resizes. Only the WINDOW is
  // tracked here: the remembered preference is untouched, so widening the
  // window again restores the width the user chose (Task 8).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // A width the user drags to is remembered as-is (already inside the bounds
  // of the window it was chosen in).
  const onResizeWidth = useCallback(
    (next: number) => {
      const chosen = clampPlayerAIRailWidth(next, viewportWidth);
      setPreferredWidth(chosen);
      try {
        window.localStorage.setItem(RAIL_WIDTH_KEY, String(chosen));
      } catch {
        // Storage is a preference; the pane still resizes without it.
      }
    },
    [viewportWidth],
  );

  // Hydrate threads + active thread id whenever the scope changes.
  // Both pieces are stored together under one key so reload restores a
  // consistent (threads, activeId) pair.
  useEffect(() => {
    if (controlledMode) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset local thread state when the rail switches into controlled (parent-owned) mode.
      setThreads([]);
      setActiveThreadId(newThreadId());
      setHistoryOpen(false);
      hydratedScopeRef.current = scope;
      return;
    }
    if (typeof window === "undefined") return;
    let nextThreads: PlayerAIThread[] = [];
    let nextActiveId: string | null = null;
    let _hadSavedRecord = false;
    try {
      const raw = window.localStorage.getItem(`${THREADS_KEY_PREFIX}${scope}`);
      if (raw) {
        _hadSavedRecord = true;
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed?.threads)) nextThreads = parsed.threads;
        if (typeof parsed?.activeId === "string")
          nextActiveId = parsed.activeId;
      }
    } catch {
      // ignore corrupt storage
    }
    // Always merge dummy sample threads into the saved list so the History
    // view is never bare. Sample threads use deterministic ids of the form
    // `sample_<scope>_<suffix>`: any sample already saved keeps its saved
    // copy (preserving its original `updatedAt`), and any sample missing
    // from the saved list is prepended fresh. The persist effect writes
    // the merged list back so subsequent reloads stay stable.
    const samples = buildSampleThreads(scope, context, Date.now());
    const savedIds = new Set(nextThreads.map((t) => t.id));
    const missingSamples = samples.filter((s) => !savedIds.has(s.id));
    if (missingSamples.length > 0) {
      nextThreads = [...missingSamples, ...nextThreads];
    }
    setThreads(nextThreads);
    // Invalidate any in-flight assistant reply from a prior scope.
    replyTokenRef.current++;
    setThinking(false);
    setDraft("");
    setPastedContext([]);
    setEditingPastedContextId(null);
    setSelectedMentions([]);
    setMentionRange(null);
    setSlashRange(null);
    setHasSelectedNextPhaseCommand(false);
    const restored = nextActiveId
      ? nextThreads.find((t) => t.id === nextActiveId)
      : undefined;
    if (restored) {
      setActiveThreadId(restored.id);
      setThread(restored.messages);
      setShowSuggestions(false);
      pristineRef.current = false;
    } else {
      // No saved thread for this scope — start fresh from the seed.
      setActiveThreadId(newThreadId());
      setThread(initialThread);
      setShowSuggestions(true);
      pristineRef.current = true;
    }
    hydratedScopeRef.current = scope;
    // initialThread intentionally omitted from deps: it's the seed used only
    // when a scope has no saved active thread; re-running on its identity
    // change would clobber an in-progress conversation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, controlledMode]);

  useEffect(() => {
    if (!draftRequest?.text.trim()) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: seed the draft textarea from an imperative parent draftRequest.
    setDraft(draftRequest.text);
    setSelectedMentions([]);
    setMentionRange(null);
    setSlashRange(null);
    setHasSelectedNextPhaseCommand(false);
    setShowSuggestions(false);
  }, [draftRequest?.id, draftRequest?.text]);

  useEffect(() => {
    if (!controlledMode) return;
    if (displayedThread.some((message) => message.role !== "system")) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: hide suggestions once the controlled thread contains a non-system message.
      setShowSuggestions(false);
    }
  }, [controlledMode, displayedThread]);

  // Persist the threads list and active thread id to localStorage as one
  // atomic record per scope (after hydration).
  useEffect(() => {
    if (controlledMode) return;
    if (typeof window === "undefined") return;
    if (hydratedScopeRef.current !== scope) return;
    try {
      window.localStorage.setItem(
        `${THREADS_KEY_PREFIX}${scope}`,
        JSON.stringify({ threads, activeId: activeThreadId }),
      );
    } catch {
      // ignore
    }
  }, [threads, activeThreadId, scope, controlledMode]);

  // Upsert the current thread into the threads list whenever it has
  // received user content. Pristine seed threads are skipped.
  useEffect(() => {
    if (controlledMode) return;
    if (pristineRef.current) return;
    if (hydratedScopeRef.current !== scope) return;
    if (!thread.some((m) => m.role === "user")) return;
    const snapshot: PlayerAIThread = {
      id: activeThreadId,
      title: deriveThreadTitle(thread, context),
      messages: thread,
      scope,
      updatedAt: Date.now(),
    };
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: upsert the active thread into local history; already a functional updater.
    setThreads((prev) => {
      const idx = prev.findIndex((t) => t.id === activeThreadId);
      if (idx >= 0) {
        const next = prev.slice();
        next[idx] = snapshot;
        return next;
      }
      return [snapshot, ...prev];
    });
  }, [thread, activeThreadId, scope, context, controlledMode]);

  // Reshow suggestions when the user navigates to a different context.
  useEffect(() => {
    if (prevKey.current === key) return;
    prevKey.current = key;
    setShowSuggestions(true);
  }, [key]);

  // Task 1 / Task 11 — follow the live end only while the reader is at it.
  // Rows hidden by "My exchanges only" never count as new messages: they only
  // update their hidden-discussion summary.
  const myExchangesActive = myExchangesOnly && groupConversation;
  const countedRows = useMemo(
    () =>
      myExchangesActive
        ? displayedThread.filter((message) => message.exchange !== "other")
        : displayedThread,
    [displayedThread, myExchangesActive],
  );
  const scroll = useChatScrollFollow({
    scrollRef,
    contentRef: threadContentRef,
    rows: countedRows,
    resetKey: displayedActiveThreadId,
  });
  const { jumpToLatest } = scroll;
  const activeReply =
    replyTarget !== null && replyTarget.threadId === displayedActiveThreadId
      ? replyTarget
      : null;
  const onReplyToMessage = useCallback(
    (message: PlayerAIChatMessage) => {
      setReplyTarget({
        messageId: message.id,
        text: (message.content ?? "").replace(/\s+/g, " ").trim(),
        threadId: displayedActiveThreadId,
      });
      composerRef.current?.focus();
    },
    [displayedActiveThreadId],
  );

  useEffect(() => {
    if (
      !open ||
      variant !== "expanded" ||
      mode === "fullscreen" ||
      initialFocus !== "composer"
    ) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      const composer = composerRef.current;
      const target =
        composer && !composer.disabled ? composer : closeButtonRef.current;
      target?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [initialFocus, mode, open, variant]);

  // When Player AI finishes answering, hand focus back to the composer. A
  // private conversation disables it for the whole Run, so the browser has
  // already dropped focus to <body>; a group conversation keeps it open, and
  // the answer still lands with focus wherever it was left.
  //
  // Keyed to the RUN, never to the thinking indicator: `isThinking` is
  // presentation, suppressed for `waiting_approval`, `waiting_documents`, the
  // activity timeline and the streamed draft, so its false edge is not
  // completion and may never arrive at all. It is equally not keyed to who
  // sent the message — every participant watching the Run gets focus back on
  // the same terms, which is the whole point for a group conversation.
  const restoreComposerFocusRef = useRef(false);
  const restoreComposerFocusConversationRef = useRef<string | null | undefined>(
    undefined,
  );
  useEffect(() => {
    if (!open) {
      restoreComposerFocusRef.current = false;
      return;
    }
    if (isRunActive) {
      restoreComposerFocusRef.current = true;
      restoreComposerFocusConversationRef.current = controlledActiveThreadId;
      return;
    }
    if (!restoreComposerFocusRef.current) return;
    // A Run finishing in the conversation the user has left must never focus
    // the one that replaced it. Compared against the conversation the Run was
    // armed in, so the rail's own thread-id churn cannot disarm it, and a
    // momentarily absent id is not read as a different conversation.
    if (
      controlledActiveThreadId != null &&
      restoreComposerFocusConversationRef.current !== controlledActiveThreadId
    ) {
      restoreComposerFocusRef.current = false;
      return;
    }
    // Wait for the composer: a read-only or still-submitting one is not
    // somewhere focus can go yet.
    if (composerDisabled) return;
    // One restore per Run, landed or not — a composer that is hidden or
    // unfocusable now must never focus itself later.
    restoreComposerFocusRef.current = false;
    // A hidden tab or an unfocused window is never pulled into focus.
    if (!document.hasFocus() || document.visibilityState === "hidden") return;
    // An open modal owns focus even in the gap before its trap has moved
    // anything there (useFocusTrap lands on a timer), and it inerts this rail
    // meanwhile.
    if (hasModalLayer()) return;
    // Whatever the user moved to keeps focus — another field, editable
    // content, a widget editor, an approval control. Only an idle document is
    // restored: focus sits on <body> and nothing has claimed it since.
    const active = document.activeElement;
    if (active !== null && active !== document.body) return;
    composerRef.current?.focus({ preventScroll: true });
  }, [composerDisabled, controlledActiveThreadId, isRunActive, open]);

  const composerSnapshotRef = useRef({
    threadId: displayedActiveThreadId,
    scope,
    draft,
  });
  useLayoutEffect(() => {
    composerSnapshotRef.current = {
      threadId: displayedActiveThreadId,
      scope,
      draft,
    };
  }, [displayedActiveThreadId, scope, draft]);
  useEffect(() => {
    // Never carry a pasted source into a different conversation.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset unsent context when its owning conversation changes.
    setPastedContext([]);
    setEditingPastedContextId(null);
  }, [displayedActiveThreadId, scope]);

  // External coach event: append an assistant-authored message to the
  // current thread without coupling this UI component to a product module.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onCoach = (event: Event) => {
      const detail = (event as CustomEvent<{ content?: string }>).detail;
      const content = detail?.content?.trim();
      if (!content) return;
      pristineRef.current = false;
      setShowSuggestions(true);
      setThread((current) => [
        ...current,
        { id: nextId(), role: "assistant", content },
      ]);
    };
    window.addEventListener("playerai:coach", onCoach as EventListener);
    return () => {
      window.removeEventListener("playerai:coach", onCoach as EventListener);
    };
  }, []);

  const send = useCallback(
    async (text: string): Promise<void> => {
      if (composerDisabled || composerReadOnly) return;
      const trimmed = text.trim();
      // Attached context is a message on its own: someone can paste a report
      // and send it with nothing typed.
      const composed = composeMessageWithPastedContext(text, pastedContext);
      if (!composed) return;
      if (
        composerMaxLength !== undefined &&
        composed.length > composerMaxLength
      ) {
        return;
      }
      // `/next_phase` is a deterministic command, not a message with a
      // payload. Attached context would make the text something else, so the
      // command simply does not apply while a pill is present.
      const sendsNextPhaseCommand =
        hasSelectedNextPhaseCommand &&
        trimmed === "/next_phase" &&
        pastedContext.length === 0;
      pristineRef.current = false;
      setShowSuggestions(false);
      // Sending rejoins the live conversation (Task 1): back to the end,
      // count cleared, following again — before the message even lands.
      jumpToLatest();
      if (controlledMode) {
        if (controlledSendPendingRef.current) return;
        controlledSendPendingRef.current = true;
        const sentSnapshot = composerSnapshotRef.current;
        const playerAIRanges = selectedMentions.filter(
          (mention) => mention.target.kind === "player_ai",
        );
        const hasSelectedPlayerAIMention = playerAIRanges.length > 0;
        const personMentions = personMentionsEnabled
          ? selectedPersonMentions(draft, selectedMentions)
          : [];
        const sentReply = activeReply;
        const extra: PlayerAISendOptions = {
          ...(personMentions.length > 0 ? { personMentions } : {}),
          ...(sentReply ? { replyToMessageId: sentReply.messageId } : {}),
        };
        try {
          if (playerAIMentionEnabled) {
            await onSendMessage?.(composed, {
              ...(hasSelectedPlayerAIMention ? { mentionsPlayerAI: true } : {}),
              ...(hasSelectedPlayerAIMention
                ? {
                    playerAIMentionOccurrenceIndices: mentionOccurrenceIndices(
                      draft,
                      PLAYER_AI_MENTION_TOKEN,
                      playerAIRanges,
                    ),
                  }
                : {}),
              ...extra,
              ...(sendsNextPhaseCommand
                ? { command: "next_phase" as const }
                : {}),
            });
          } else if (sendsNextPhaseCommand) {
            await onSendMessage?.(composed, {
              ...extra,
              command: "next_phase",
            });
          } else if (Object.keys(extra).length > 0) {
            await onSendMessage?.(composed, extra);
          } else {
            await onSendMessage?.(composed);
          }
          onComposerActivity?.("idle");
          const current = composerSnapshotRef.current;
          if (
            current.threadId === sentSnapshot.threadId &&
            current.scope === sentSnapshot.scope
          ) {
            // Consume only the exact items sent. New or edited pastes belong
            // to the next message and must survive this acknowledgement.
            setPastedContext((items) =>
              items.filter((item) => !pastedContext.includes(item)),
            );
            // The reply was sent with this message; a different one chosen
            // meanwhile belongs to the next.
            setReplyTarget((currentReply) =>
              currentReply?.messageId === sentReply?.messageId
                ? null
                : currentReply,
            );
            if (current.draft === sentSnapshot.draft) {
              setDraft("");
              setSelectedMentions([]);
              setMentionRange(null);
              setSlashRange(null);
              setHasSelectedNextPhaseCommand(false);
            }
          }
        } catch {
          // The host owns actionable request errors. Preserve the draft AND
          // the attached context so the user can retry without pasting again.
        } finally {
          controlledSendPendingRef.current = false;
        }
        return;
      }
      setDraft("");
      setPastedContext([]);
      setEditingPastedContextId(null);
      setSelectedMentions([]);
      setMentionRange(null);
      setSlashRange(null);
      setHasSelectedNextPhaseCommand(false);
      setThread((t) => [
        ...t,
        { id: nextId(), role: "user", content: composed },
      ]);
      setThinking(true);
      const myToken = ++replyTokenRef.current;
      window.setTimeout(async () => {
        // Bail out if the user started a new/restored thread or switched
        // scope while this reply was pending.
        if (replyTokenRef.current !== myToken) return;
        let raw = resolveReply?.(composed, context);
        // Allow async resolvers (e.g. WebSocket-backed). When the resolver
        // returns a Promise, await it and re-check the token after to avoid
        // dropping the reply into a stale thread.
        if (raw instanceof Promise) {
          try {
            raw = await raw;
          } catch (err) {
            raw = `Error: ${err instanceof Error ? err.message : String(err)}`;
          }
          if (replyTokenRef.current !== myToken) return;
        }
        let result: PlayerAIReplyResult;
        if (raw == null) {
          result = {
            content:
              "I don't have a scripted response yet — but I've noted your prompt.",
          };
        } else if (typeof raw === "string") {
          result = { content: raw };
        } else {
          result = raw;
        }
        const assistantMessage: PlayerAIChatMessage = {
          id: nextId(),
          role: "assistant",
          ...(result.content !== undefined ? { content: result.content } : {}),
          ...(result.widget !== undefined ? { widget: result.widget } : {}),
        };
        setThread((t) => [...t, assistantMessage]);
        setThinking(false);
      }, 550);
    },
    [
      composerDisabled,
      composerReadOnly,
      controlledMode,
      onSendMessage,
      onComposerActivity,
      playerAIMentionEnabled,
      personMentionsEnabled,
      hasSelectedNextPhaseCommand,
      composerMaxLength,
      pastedContext,
      draft,
      selectedMentions,
      activeReply,
      jumpToLatest,
      resolveReply,
      context,
    ],
  );

  const updateDraft = useCallback(
    (value: string, caret: number, inputType?: string) => {
      setDraft(value);
      // Task 15 — the host turns this into throttled, expiring typing signals;
      // the draft's TEXT never leaves the composer.
      onComposerActivity?.(value.trim() ? "typing" : "idle");
      if (selectedMentions.length > 0) {
        setSelectedMentions(
          reconcileSelectedMentionRanges(draft, value, selectedMentions),
        );
      }
      if (hasSelectedNextPhaseCommand && value.trim() !== "/next_phase") {
        setHasSelectedNextPhaseCommand(false);
      }
      if (nextPhaseCommandEnabled && !inputType?.startsWith("insertFrom")) {
        const prefix = value.slice(0, caret);
        const slashMatch = /^\/([a-z_]*)$/.exec(prefix);
        const query = slashMatch?.[1]?.toLowerCase() ?? "";
        if (slashMatch && "next_phase".startsWith(query)) {
          if (
            query === "next_phase" &&
            value.trim() === "/next_phase" &&
            inputType === "insertText"
          ) {
            setHasSelectedNextPhaseCommand(true);
            setSlashRange(null);
          } else {
            setHasSelectedNextPhaseCommand(false);
            setSlashRange({ start: 0, end: caret });
          }
        } else {
          setSlashRange(null);
        }
      } else {
        setSlashRange(null);
      }
      if (!mentionPickerEnabled) {
        setMentionRange(null);
        return;
      }
      const prefix = value.slice(0, caret);
      const match = /(^|\s)@([^\s@]*)$/.exec(prefix);
      const query = match?.[2] ?? "";
      // The host searches for people as the query changes; their answer
      // arrives after this keystroke, so an open `@` stays open for them.
      onPersonMentionQuery?.(match ? query : null);
      if (
        !match ||
        (!onPersonMentionQuery &&
          mentionOptionsFor(query, playerAIMentionEnabled, []).length === 0)
      ) {
        setMentionRange(null);
        return;
      }
      setMentionRange({ start: caret - query.length - 1, end: caret });
      setActiveMentionIndex(0);
    },
    [
      draft,
      hasSelectedNextPhaseCommand,
      mentionPickerEnabled,
      nextPhaseCommandEnabled,
      onComposerActivity,
      onPersonMentionQuery,
      playerAIMentionEnabled,
      selectedMentions,
    ],
  );

  const previousPersonMentionQuery = useRef(onPersonMentionQuery);
  useEffect(() => {
    if (previousPersonMentionQuery.current !== onPersonMentionQuery) {
      previousPersonMentionQuery.current?.(null);
      onPersonMentionQuery?.(
        mentionRange === null
          ? null
          : draft.slice(mentionRange.start + 1, mentionRange.end),
      );
      previousPersonMentionQuery.current = onPersonMentionQuery;
    }
  }, [draft, mentionRange, onPersonMentionQuery]);

  const previousMentionRange = useRef<typeof mentionRange>(null);
  useEffect(() => {
    if (previousMentionRange.current !== null && mentionRange === null) {
      onPersonMentionQuery?.(null);
    }
    previousMentionRange.current = mentionRange;
  }, [mentionRange, onPersonMentionQuery]);

  const mentionOptions = useMemo(
    () =>
      mentionRange === null
        ? []
        : mentionOptionsFor(
            draft.slice(mentionRange.start + 1, mentionRange.end),
            playerAIMentionEnabled,
            personMentionOptions,
          ),
    [draft, mentionRange, personMentionOptions, playerAIMentionEnabled],
  );
  const boundedMentionIndex =
    mentionOptions.length === 0
      ? 0
      : Math.min(activeMentionIndex, mentionOptions.length - 1);

  const selectMention = useCallback(
    (option: ComposerMentionOption) => {
      if (!mentionRange) return;
      const token =
        option.kind === "player_ai"
          ? PLAYER_AI_MENTION_TOKEN
          : `@${option.label}`;
      const suffix = draft.slice(mentionRange.end);
      const separator =
        suffix.length === 0 ? " " : /^\s/.test(suffix) ? "" : " ";
      const nextDraft = `${draft.slice(0, mentionRange.start)}${token}${separator}${suffix}`;
      const nextCursor = mentionRange.start + token.length + separator.length;
      // Everything after the replaced query shifts by the same amount, so the
      // selections already made stay on their own characters.
      const shift =
        token.length +
        separator.length -
        (mentionRange.end - mentionRange.start);
      pendingComposerCursorRef.current = nextCursor;
      setDraft(nextDraft);
      setSelectedMentions((current) => [
        ...current.map((mention) =>
          mention.start >= mentionRange.end
            ? {
                ...mention,
                start: mention.start + shift,
                end: mention.end + shift,
              }
            : mention,
        ),
        {
          start: mentionRange.start,
          end: mentionRange.start + token.length,
          target:
            option.kind === "player_ai"
              ? { kind: "player_ai" }
              : { kind: "person", userId: option.id, token },
        },
      ]);
      setMentionRange(null);
    },
    [draft, mentionRange],
  );

  const selectNextPhaseCommand = useCallback(() => {
    if (!slashRange) return;
    const nextDraft = "/next_phase ";
    pendingComposerCursorRef.current = nextDraft.length;
    setDraft(nextDraft);
    setHasSelectedNextPhaseCommand(true);
    setSlashRange(null);
  }, [slashRange]);

  useLayoutEffect(() => {
    const nextCursor = pendingComposerCursorRef.current;
    if (nextCursor === null) return;
    pendingComposerCursorRef.current = null;
    composerRef.current?.focus();
    composerRef.current?.setSelectionRange(nextCursor, nextCursor);
  }, [draft]);

  const contextLength = useMemo(
    () => composeMessageWithPastedContext("", pastedContext).length,
    [pastedContext],
  );
  const typedLength = draft.trim().length;
  const composedLength =
    typedLength +
    contextLength +
    (typedLength > 0 && contextLength > 0 ? 2 : 0);
  const editingPastedContextIndex = pastedContext.findIndex(
    (item) => item.id === editingPastedContextId,
  );
  const composerPastedContext: ComposerPastedContext = {
    items: pastedContext,
    editingIndex: editingPastedContextIndex,
    editing:
      editingPastedContextIndex >= 0
        ? pastedContext[editingPastedContextIndex]
        : undefined,
    // Said out loud rather than enforced by trimming. The composer refuses the
    // send and keeps every character, so the user chooses what to cut.
    overLimit:
      composerMaxLength !== undefined && composedLength > composerMaxLength,
    composedLength: composedLength,
    maxLength: composerMaxLength,
    onPaste: (event) => {
      const pasted = event.clipboardData.getData("text/plain");
      if (pasted.length <= PASTED_CONTEXT_THRESHOLD) return;
      // Past the threshold the composer keeps the question and the paste
      // becomes attached context. Verbatim — newlines, trailing spaces and all.
      event.preventDefault();
      setPastedContext((current) => [
        ...current,
        { id: nextId(), text: pasted },
      ]);
    },
    onEdit: setEditingPastedContextId,
    onRemove: (id) => {
      setPastedContext((current) => current.filter((item) => item.id !== id));
      setEditingPastedContextId((current) => (current === id ? null : current));
    },
    onSaveEdit: (next) => {
      const id = pastedContext[editingPastedContextIndex]?.id;
      if (id === undefined) return;
      // An emptied field removes the pill: attaching nothing is not a thing.
      setPastedContext((current) =>
        next.length === 0
          ? current.filter((item) => item.id !== id)
          : current.map((item) =>
              item.id === id ? { ...item, text: next } : item,
            ),
      );
      setEditingPastedContextId(null);
    },
    onCancelEdit: () => setEditingPastedContextId(null),
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void send(draft);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (composerReadOnly) return;
    if (slashRange) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        return;
      }
      if (
        (e.key === "Enter" || e.key === "Tab") &&
        !e.nativeEvent.isComposing
      ) {
        e.preventDefault();
        selectNextPhaseCommand();
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setSlashRange(null);
        return;
      }
    }
    if (mentionRange && e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setMentionRange(null);
      return;
    }
    if (mentionRange && mentionOptions.length > 0) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const step = e.key === "ArrowDown" ? 1 : -1;
        setActiveMentionIndex(
          (boundedMentionIndex + step + mentionOptions.length) %
            mentionOptions.length,
        );
        return;
      }
      if (
        (e.key === "Enter" || e.key === "Tab") &&
        !e.nativeEvent.isComposing
      ) {
        e.preventDefault();
        const option = mentionOptions[boundedMentionIndex];
        if (option) selectMention(option);
        return;
      }
    }
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      if (draft.length > 0) {
        setDraft("");
        onComposerActivity?.("idle");
        setSelectedMentions([]);
        setMentionRange(null);
        setSlashRange(null);
        setHasSelectedNextPhaseCommand(false);
      } else {
        onToggle?.();
      }
      return;
    }
    if (
      e.key === "Enter" &&
      (e.metaKey || e.ctrlKey) &&
      !e.nativeEvent.isComposing
    ) {
      e.preventDefault();
      const start = e.currentTarget.selectionStart;
      const end = e.currentTarget.selectionEnd;
      const nextDraft = `${draft.slice(0, start)}\n${draft.slice(end)}`;
      const nextCursor = start + 1;
      setDraft(nextDraft);
      requestAnimationFrame(() => {
        composerRef.current?.setSelectionRange(nextCursor, nextCursor);
      });
      return;
    }
    if (
      e.key === "Enter" &&
      !e.metaKey &&
      !e.ctrlKey &&
      !e.shiftKey &&
      !e.altKey &&
      !e.nativeEvent.isComposing
    ) {
      e.preventDefault();
      void send(draft);
    }
  };

  const onNewThread = useCallback(() => {
    if (backendHistoryMode && onNewControlledThread) {
      replyTokenRef.current++;
      setHistoryOpen(false);
      setShowSuggestions(true);
      setDraft("");
      setSelectedMentions([]);
      setMentionRange(null);
      setTab("chat");
      void onNewControlledThread();
      return;
    }
    // The current thread (if it had user messages) is already archived in
    // `threads` via the upsert effect. Reset to a fresh seed state and
    // invalidate any pending assistant reply.
    replyTokenRef.current++;
    setHistoryOpen(false);
    setActiveThreadId(newThreadId());
    pristineRef.current = true;
    setThread(initialThread);
    setShowSuggestions(true);
    setDraft("");
    setSelectedMentions([]);
    setMentionRange(null);
    setSlashRange(null);
    setHasSelectedNextPhaseCommand(false);
    setThinking(false);
    setTab("chat");
  }, [backendHistoryMode, initialThread, onNewControlledThread]);

  const onSelectThread = useCallback(
    (t: PlayerAIThread) => {
      if (backendHistoryMode && onSelectControlledThread) {
        replyTokenRef.current++;
        setHistoryOpen(false);
        setShowSuggestions(false);
        setDraft("");
        setSelectedMentions([]);
        setMentionRange(null);
        setSlashRange(null);
        setHasSelectedNextPhaseCommand(false);
        setThinking(false);
        setTab("chat");
        void onSelectControlledThread(t);
        return;
      }
      replyTokenRef.current++;
      setHistoryOpen(false);
      setActiveThreadId(t.id);
      pristineRef.current = false;
      setThread(t.messages);
      setShowSuggestions(false);
      setDraft("");
      setSelectedMentions([]);
      setMentionRange(null);
      setSlashRange(null);
      setHasSelectedNextPhaseCommand(false);
      setThinking(false);
      setTab("chat");
    },
    [backendHistoryMode, onSelectControlledThread],
  );

  const onDeleteHistoryThread = useCallback(
    (threadId: string) => {
      if (backendHistoryMode && onDeleteThread) {
        void onDeleteThread(threadId);
        return;
      }
      setThreads((prev) => prev.filter((t) => t.id !== threadId));
      if (threadId === activeThreadId) {
        onNewThread();
      }
    },
    [activeThreadId, backendHistoryMode, onDeleteThread, onNewThread],
  );

  const compactIcon = (
    id: PlayerAITabId,
    Icon: typeof Sparkles,
    label: string,
    accent?: boolean,
  ) => (
    <Tooltip key={id} content={label} describeChild={false} placement="left">
      <button
        onClick={() => {
          compactTabRef.current = id;
          setTab(id);
          onToggle?.();
        }}
        aria-label={label}
        className={`w-9 h-9 rounded-md flex items-center justify-center transition-colors border ${
          accent
            ? "text-pb-iris-text border-pb-iris/30 bg-pb-iris-soft/30 hover:bg-pb-iris-soft/50"
            : "text-pb-text-secondary border-transparent hover:text-pb-text hover:pb-overlay-paint hover:border-pb-control-border"
        }`}
      >
        <Icon className="w-5 h-5" />
      </button>
    </Tooltip>
  );

  if (variant === "compact") {
    return (
      <div className="w-14 h-full bg-pb-rail border-s border-pb-border-strong flex flex-col items-center py-3 gap-2 shadow-sm overflow-hidden shrink-0">
        <Tooltip
          content="Expand PlayerAI rail"
          describeChild={false}
          placement="left"
        >
          <button
            onClick={onToggle}
            aria-label="Expand PlayerAI rail"
            className="w-8 h-8 rounded-md flex items-center justify-center text-pb-text-muted hover:text-pb-text hover:pb-overlay-paint border border-pb-control-border transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        </Tooltip>
        <div className="w-8 h-px bg-pb-border my-1" />
        {compactIcon("chat", Sparkles, "Chat with PlayerAI", true)}
        {compactIcon("activity", Activity, "Activity")}
        {compactIcon("actions", Zap, "Quick actions")}
        {compactIcon("hoshin", Compass, "Hoshin payload")}
      </div>
    );
  }

  // Mode-switch icon button (used in every mode's header).
  const headerIconBtnClass =
    "w-7 h-7 flex items-center justify-center rounded hover:pb-overlay-paint text-pb-text-secondary hover:text-pb-text transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-iris focus-visible:ring-offset-1 focus-visible:ring-offset-pb-rail";
  const modeBtn = (
    target: PlayerAIMode,
    Icon: typeof MessageSquare,
    label: string,
  ) => (
    <Tooltip key={target} content={label} describeChild={false}>
      <button
        type="button"
        onClick={() => onSetMode?.(target)}
        aria-label={label}
        className={headerIconBtnClass}
      >
        <Icon className="w-3.5 h-3.5" />
      </button>
    </Tooltip>
  );
  const modeSwitches = (
    <>
      {mode !== "popover" &&
        modeBtn("popover", MessageSquare, "Open as popover")}
      {mode !== "rail" && modeBtn("rail", PanelRight, "Open in side rail")}
      {mode !== "fullscreen" &&
        modeBtn("fullscreen", Maximize2, "Expand to fullscreen")}
    </>
  );
  // Task 6 — the viewer's mentions, at the start of the existing header row.
  const mentionNavigator = mentionNavigation ? (
    <MentionNavigator
      navigation={mentionNavigation}
      visibleRange={scroll.visibleSequenceRange}
    />
  ) : null;
  // Header bar used by popover & rail. In fullscreen the header lives
  // inside the right column and the Conversations toggle is omitted.
  const headerBar = (
    <div
      data-playerai-header
      className="h-11 px-3 flex items-center gap-1 border-b border-pb-border/50 shrink-0"
    >
      {historyOpen ? (
        <>
          <Tooltip content="Back to chat" describeChild={false}>
            <button
              onClick={() => setHistoryOpen(false)}
              aria-label="Back to chat"
              className={headerIconBtnClass}
            >
              <ArrowLeft className="w-3.5 h-3.5" />
            </button>
          </Tooltip>
          <span className="text-pb-text text-xs font-medium">
            Conversations
          </span>
        </>
      ) : tab !== "chat" ? (
        <span className="text-pb-text-muted text-xs">· {tabLabel(tab)}</span>
      ) : (
        mentionNavigator
      )}
      <div className="flex-1" />
      {tab === "chat" && !historyOpen && canUseHistory && (
        <>
          <Tooltip content="New conversation" describeChild={false}>
            <button
              onClick={onNewThread}
              aria-label="New conversation"
              className={headerIconBtnClass}
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </Tooltip>
          <Tooltip content="Conversation history" describeChild={false}>
            <button
              onClick={() => setHistoryOpen(true)}
              aria-label="Conversation history"
              className={headerIconBtnClass}
            >
              <History className="w-3.5 h-3.5" />
            </button>
          </Tooltip>
          <div className="w-px h-4 bg-pb-border/60 mx-0.5" />
        </>
      )}
      {!historyOpen ? headerActionsSlot : null}
      {modeSwitches}
      <Tooltip content="Close Player AI" describeChild={false}>
        <button
          ref={closeButtonRef}
          onClick={onToggle}
          aria-label="Close Player AI"
          className={headerIconBtnClass}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </Tooltip>
    </div>
  );

  const historyDeleteProps =
    !backendHistoryMode || onDeleteThread
      ? { onDelete: onDeleteHistoryThread }
      : {};
  // ONE chat body for every presentation, so the rail, the popover and
  // fullscreen cannot disagree about what the composer offers.
  const chatBody = (
    <ChatBody
      pastedContext={composerPastedContext}
      thread={displayedThread}
      thinking={displayedThinking}
      showSuggestions={showSuggestions}
      suggestedPrompts={suggestedPrompts}
      draft={draft}
      setDraft={updateDraft}
      onSubmit={onSubmit}
      onKeyDown={onKeyDown}
      onPickPrompt={send}
      scrollRef={scrollRef}
      focusedRequestRef={focusedRequestRef}
      threadContentRef={threadContentRef}
      scroll={scroll}
      hasMoreHistory={hasMoreHistory}
      isLoadingHistory={isLoadingHistory}
      onLoadOlderHistory={onLoadOlderHistory}
      onWidgetAction={onWidgetAction}
      onMessageFeedback={onMessageFeedback}
      onReply={onReplyToMessage}
      onRequestMessage={onRequestMessage}
      focusRequest={focusRequest}
      myExchangesOnly={myExchangesActive}
      composerRef={composerRef}
      composerDisabled={composerDisabled}
      composerReadOnly={composerReadOnly}
      composerPlaceholder={composerPlaceholder}
      showConversationSkeleton={showConversationSkeleton}
      composerStatusSlot={composerStatusSlot}
      composerMetadataSlot={composerMetadataSlot}
      composerContextAttachments={composerContextAttachments}
      onRemoveContextAttachment={onRemoveContextAttachment}
      composerContextNotice={composerContextNotice}
      composerLeadingSlot={composerLeadingSlot}
      mentions={{
        available: mentionPickerEnabled,
        menuOpen:
          mentionPickerEnabled &&
          mentionRange !== null &&
          (mentionOptions.length > 0 || personMentions?.status !== undefined),
        options: mentionOptions,
        status: groupConversation ? personMentions?.status : undefined,
        onRetry: groupConversation ? personMentions?.onRetry : undefined,
        activeIndex: boundedMentionIndex,
        onSelect: selectMention,
      }}
      nextPhaseCommand={
        nextPhaseCommandEnabled ? { available: true } : undefined
      }
      slashMenuOpen={nextPhaseCommandEnabled && slashRange !== null}
      onSelectNextPhaseCommand={selectNextPhaseCommand}
      groupConversation={groupConversation}
      composerFooterSlot={composerFooterSlot}
      composerHintSlot={composerHintSlot}
      reply={
        activeReply
          ? { text: activeReply.text, onCancel: () => setReplyTarget(null) }
          : undefined
      }
      typingNames={groupConversation ? typingNames : undefined}
      runControl={runControl}
      pauseNotice={pauseNotice}
      approvalNotice={approvalNotice}
    />
  );
  const body =
    historyOpen && mode !== "fullscreen" ? (
      <HistoryView
        threads={historyThreads}
        activeThreadId={displayedActiveThreadId ?? ""}
        onSelect={onSelectThread}
        onNewThread={onNewThread}
        {...historyDeleteProps}
        onClose={() => setHistoryOpen(false)}
      />
    ) : tab === "chat" ? (
      chatBody
    ) : tab === "hoshin" && hoshinPayload ? (
      <HoshinPayloadBody payload={hoshinPayload} />
    ) : (
      <PlaceholderBody tab={tab} />
    );

  if (mode === "popover") {
    return (
      <PlayerAIPopoverShell
        open={open}
        onExited={onExited}
        {...(onToggle !== undefined ? { onClose: onToggle } : {})}
      >
        {headerBar}
        {body}
      </PlayerAIPopoverShell>
    );
  }

  if (mode === "fullscreen") {
    // Fullscreen header in the right column omits the Conversations
    // toggle (the list is always visible on the left) and the back
    // arrow (no history overlay needed).
    const fsHeader = (
      <div
        data-playerai-header
        className="h-11 px-3 flex items-center gap-1 border-b border-pb-border/50 shrink-0"
      >
        {tab !== "chat" ? (
          <span className="text-pb-text-muted text-xs">· {tabLabel(tab)}</span>
        ) : (
          mentionNavigator
        )}
        <div className="flex-1" />
        {headerActionsSlot}
        {modeSwitches}
        <Tooltip content="Close Player AI" describeChild={false}>
          <button
            onClick={onToggle}
            aria-label="Close Player AI"
            className={headerIconBtnClass}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </Tooltip>
      </div>
    );
    return (
      <PlayerAIFullscreenShell
        open={open}
        onExited={onExited}
        initialFocusRef={
          initialFocus === "composer" && !composerDisabled
            ? composerRef
            : undefined
        }
        {...(onToggle !== undefined ? { onClose: onToggle } : {})}
      >
        {/* Left: conversations sidebar */}
        <div className="border-b md:border-b-0 md:border-e border-pb-border/60 flex flex-col min-h-0 bg-pb-rail">
          <div
            data-playerai-header
            className="h-11 px-3 flex items-center gap-1 border-b border-pb-border/50 shrink-0"
          >
            <span className="text-pb-text text-xs font-medium">
              Conversations
            </span>
            <div className="flex-1" />
            {canUseHistory && (
              <Tooltip content="New conversation" describeChild={false}>
                <button
                  onClick={onNewThread}
                  aria-label="New conversation"
                  className={headerIconBtnClass}
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </Tooltip>
            )}
          </div>
          <HistoryView
            threads={historyThreads}
            activeThreadId={displayedActiveThreadId ?? ""}
            onSelect={onSelectThread}
            onNewThread={onNewThread}
            {...historyDeleteProps}
            // Esc inside the search input clears it first, then a second
            // Esc on an empty input falls through to onClose — wire that
            // to dismiss the fullscreen modal so keyboard users can close
            // without first refocusing the backdrop.
            onClose={() => onToggle?.()}
          />
        </div>
        {/* Right: chat */}
        <div className="flex flex-col min-h-0 bg-pb-rail">
          {fsHeader}
          {/* Constrain chat content to a comfortable reading width inside
              the wider fullscreen column so message bubbles and inline
              widgets (e.g. the KPI list) do not stretch edge-to-edge.
              The header above stays full-width. */}
          <div className="flex-1 flex flex-col min-h-0 w-full max-w-4xl mx-auto">
            {tab === "chat" ? (
              chatBody
            ) : tab === "hoshin" && hoshinPayload ? (
              <HoshinPayloadBody payload={hoshinPayload} />
            ) : (
              <PlaceholderBody tab={tab} />
            )}
          </div>
        </div>
      </PlayerAIFullscreenShell>
    );
  }

  // mode === "rail" (default)
  return (
    <PlayerAIDockedShell
      open={open}
      onExited={onExited}
      width={width}
      resizeHandle={
        <ResizeHandle
          label="Resize PlayerAI pane"
          tone="ai"
          edge="start"
          value={width}
          min={PLAYER_AI_RAIL_MIN_WIDTH}
          max={clampPlayerAIRailWidth(Number.MAX_SAFE_INTEGER, viewportWidth)}
          onChange={onResizeWidth}
        />
      }
    >
      {headerBar}
      {body}
    </PlayerAIDockedShell>
  );
}

/** The reservation grows with the slide; the thread retains its reading width.
 * Only the body is clipped, so the splitter keeps its full 44px hit area. */
function PlayerAIDockedShell({
  open,
  onExited,
  width,
  resizeHandle,
  children,
}: {
  open: boolean;
  onExited?: (() => void) | undefined;
  width: number;
  resizeHandle: ReactNode;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const { present, phase, motionProps } = useOverlayPresence(open, {
    ref,
    preset: "docked-panel",
    exitDurationMs: MOTION_DURATION_MS.layout,
  });
  useEffect(() => {
    if (!present) onExited?.();
  }, [onExited, present]);
  if (!present) return null;
  return (
    <div
      ref={ref}
      {...motionProps}
      data-playerai-rail-slot
      className={`relative max-w-full shrink-0 h-full shadow-2xl ${motionProps.className}`}
      style={{ "--pb-panel-inline-size": `${width}px` } as CSSProperties}
    >
      <div data-pb-panel-clip className="absolute inset-0 overflow-clip">
        <aside
          data-playerai-surface="rail"
          data-motion-state={phase}
          data-tour-id="shell.player-ai-pane"
          aria-label="PlayerAI"
          className="absolute end-0 top-0 bg-pb-rail border-s border-pb-border-strong flex flex-col h-full"
          style={{ width: `${width}px` }}
        >
          {children}
        </aside>
      </div>
      {resizeHandle}
    </div>
  );
}

/**
 * Floating popover container at the bottom inline-end corner. The launcher is
 * hidden while any Player AI surface is open, so the exits are Close, Esc and
 * a pointerdown outside its bounds (a press on the launcher is still ignored
 * there, which keeps a same-click toggle from reopening it).
 *
 * Its height clamps under the TopBar at every width, so its header is never
 * covered, and it clips rather than hides its overflow, so no focus or
 * scrollIntoView can scroll that header away (PB-5381).
 */
function PlayerAIPopoverShell({
  open,
  onExited,
  onClose,
  children,
}: {
  open: boolean;
  onExited?: (() => void) | undefined;
  onClose?: () => void;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const { present, motionProps } = useOverlayPresence(open, {
    ref,
    preset: "anchored-panel",
  });
  useEffect(() => {
    if (!present) onExited?.();
  }, [onExited, present]);
  useEffect(() => {
    if (!open || !onClose) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (hasOpenOverlayLayer({ ignoreTooltips: true })) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      // Let inputs/textareas swallow their own Esc (e.g. clear search).
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      e.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose, open]);
  useEffect(() => {
    if (!open || !onClose) return;
    const onPointer = (e: PointerEvent) => {
      // Portaled menus and dialogs own this press, even outside our DOM.
      if (hasOpenOverlayLayer({ ignoreTooltips: true })) return;
      const el = ref.current;
      if (!el) return;
      const target = e.target as Node | null;
      if (!target) return;
      if (el.contains(target)) return;
      // Ignore clicks on the launcher itself — it has its own toggle.
      const launcher = (target as HTMLElement).closest?.(
        "[data-playerai-launcher]",
      );
      if (launcher) return;
      onClose();
    };
    // Defer one frame so the same click that opened the popover doesn't
    // also close it.
    const id = window.setTimeout(() => {
      // Inspect the stack before a nested overlay handles and closes itself.
      document.addEventListener("pointerdown", onPointer, true);
    }, 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("pointerdown", onPointer, true);
    };
  }, [onClose, open]);
  if (!present) return null;
  return (
    <div
      ref={ref}
      {...motionProps}
      data-playerai-surface="popover"
      data-tour-id="shell.player-ai-pane"
      role="dialog"
      aria-label="PlayerAI"
      // One height rule at every width: 560px when there is room, otherwise
      // the window minus the TopBar (3rem = TopBar h-12), the bottom offset and
      // a 0.75rem gap. The offset is declared once per breakpoint and feeds both
      // `bottom` and `height`, so they cannot drift. The playerai-safe-area VR
      // gate enforces it against the real TopBar.
      className={`fixed start-3 ${PLAYER_AI_FLOATING_PLACEMENT} h-[min(560px,calc(100dvh-3rem-var(--playerai-bottom)-0.75rem))] z-40 rounded-xl bg-pb-rail border border-pb-border-strong shadow-2xl flex flex-col overflow-clip sm:start-auto sm:w-[420px] sm:rounded-2xl ${motionProps.className}`}
    >
      {children}
    </div>
  );
}

/**
 * Centered modal container for the fullscreen mode. Backdrop click and
 * Esc both close the modal; inputs are allowed to swallow their own
 * Esc first (so the search field's "clear on Esc" still works).
 */
function PlayerAIFullscreenShell({
  open,
  onExited,
  onClose,
  initialFocusRef,
  children,
}: {
  open: boolean;
  onExited?: (() => void) | undefined;
  onClose?: () => void;
  initialFocusRef?: RefObject<HTMLElement | null> | undefined;
  children: ReactNode;
}) {
  return (
    <ModalShell
      open={open}
      onClose={() => onClose?.()}
      onExited={onExited}
      label="PlayerAI"
      motionPreset="dialog-panel"
      initialFocusRef={initialFocusRef}
      containerClassName="items-center justify-center p-6"
      panelClassName="w-[min(1440px,95vw)] rounded-xl border border-pb-border-strong shadow-2xl overflow-clip"
      panelSemantics={false}
    >
      <div
        data-playerai-surface="fullscreen"
        data-tour-id="shell.player-ai-pane"
        role="dialog"
        aria-modal="true"
        aria-label="PlayerAI"
        className="relative w-full h-[92vh] bg-pb-rail grid grid-cols-1 grid-rows-[minmax(180px,34vh)_1fr] md:grid-cols-[280px_1fr] md:grid-rows-none overflow-clip"
      >
        {children}
      </div>
    </ModalShell>
  );
}

function tabLabel(t: PlayerAITabId) {
  switch (t) {
    case "activity":
      return "Activity";
    case "actions":
      return "Quick actions";
    case "hoshin":
      return "Hoshin payload";
    default:
      return "Chat";
  }
}

interface ChatBodyProps {
  pastedContext: ComposerPastedContext;
  thread: PlayerAIChatMessage[];
  thinking: boolean;
  showSuggestions: boolean;
  suggestedPrompts: PlayerAISuggestedPrompt[];
  draft: string;
  setDraft: (value: string, caret: number, inputType?: string) => void;
  onSubmit: (e: FormEvent) => void;
  onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  onPickPrompt: (text: string) => void;
  scrollRef: React.MutableRefObject<HTMLDivElement | null>;
  focusedRequestRef: React.MutableRefObject<string | null>;
  threadContentRef: RefObject<HTMLDivElement>;
  hasMoreHistory: boolean;
  isLoadingHistory: boolean;
  onLoadOlderHistory?: (() => void | Promise<void>) | undefined;
  onWidgetAction?: PlayerAIWidgetActionHandler | undefined;
  onMessageFeedback?:
    | ((
        feedback: PlayerAIMessageFeedbackRequest,
      ) => Promise<PlayerAIMessageFeedback>)
    | undefined;
  composerRef: MutableRefObject<HTMLTextAreaElement | null>;
  composerDisabled: boolean;
  composerReadOnly: boolean;
  composerPlaceholder?: string | undefined;
  showConversationSkeleton: boolean;
  composerStatusSlot?: ReactNode | undefined;
  composerMetadataSlot?: ReactNode | undefined;
  composerContextAttachments?:
    | readonly { id: string; name: string }[]
    | undefined;
  onRemoveContextAttachment?: ((id: string) => void) | undefined;
  composerContextNotice?: string | undefined;
  composerLeadingSlot?: ReactNode | undefined;
  mentions: ComposerMentions;
  nextPhaseCommand?: PlayerAIRailProps["nextPhaseCommand"];
  slashMenuOpen: boolean;
  onSelectNextPhaseCommand: () => void;
  groupConversation: boolean;
  composerFooterSlot?: ReactNode | undefined;
  composerHintSlot?: ReactNode | undefined;
  scroll: ChatScrollFollow;
  onReply: (message: PlayerAIChatMessage) => void;
  onRequestMessage?:
    | ((reference: PlayerAIMessageReference) => void)
    | undefined;
  focusRequest?: PlayerAIRailProps["focusRequest"];
  myExchangesOnly: boolean;
  reply?: { readonly text: string; readonly onCancel: () => void } | undefined;
  typingNames?: readonly string[] | undefined;
  runControl?: PlayerAIRunControl | undefined;
  pauseNotice?: PlayerAIPauseNotice | undefined;
  approvalNotice?: PlayerAIApprovalNotice | undefined;
}

function ChatBody({
  pastedContext,
  thread,
  thinking,
  showSuggestions,
  suggestedPrompts,
  draft,
  setDraft,
  onSubmit,
  onKeyDown,
  onPickPrompt,
  scrollRef,
  focusedRequestRef,
  threadContentRef,
  hasMoreHistory,
  isLoadingHistory,
  onLoadOlderHistory,
  onWidgetAction,
  onMessageFeedback,
  composerRef,
  composerDisabled,
  composerReadOnly,
  composerPlaceholder,
  showConversationSkeleton,
  composerStatusSlot,
  composerMetadataSlot,
  composerContextAttachments,
  onRemoveContextAttachment,
  composerContextNotice,
  composerLeadingSlot,
  mentions,
  nextPhaseCommand,
  slashMenuOpen,
  onSelectNextPhaseCommand,
  groupConversation,
  composerFooterSlot,
  composerHintSlot,
  scroll,
  onReply,
  onRequestMessage,
  focusRequest,
  myExchangesOnly,
  reply,
  typingNames,
  runControl,
  pauseNotice,
  approvalNotice,
}: ChatBodyProps) {
  const handleWidgetAction = onWidgetAction ?? onPickPrompt;
  const empty = useMemo(
    () => thread.every((m) => m.role === "system"),
    [thread],
  );
  const { revealRow } = scroll;
  const onScrollFollow = scroll.onScroll;

  // Task 17 — hidden stretches of other people's discussion, and which of
  // them the reader opened. A host focus request into a folded stretch opens
  // it for as long as that request stands, unless the reader folds it again.
  const entries = useMemo(
    () => foldHiddenExchanges(thread, myExchangesOnly),
    [thread, myExchangesOnly],
  );
  const [openHidden, setOpenHidden] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [dismissedFocusId, setDismissedFocusId] = useState<string | null>(null);
  const focusGroupKey =
    focusRequest && focusRequest.id !== dismissedFocusId
      ? (hiddenEntryHolding(entries, focusRequest.messageId)?.key ?? null)
      : null;
  const toggleHidden = useCallback(
    (key: string, open: boolean) => {
      if (open && key === focusGroupKey) {
        setDismissedFocusId(focusRequest?.id ?? null);
      }
      setOpenHidden((current) => {
        const next = new Set(current);
        if (open) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    [focusGroupKey, focusRequest?.id],
  );

  // Task 13 — the newest proposal section stays open; older completed ones
  // fold (the section decides, see CollapsibleProposal).
  const latestProposalId = useMemo(() => latestProposalRowId(thread), [thread]);

  // Take the reader to a message the host just made available.
  const focusRequestId = focusRequest?.id;
  const focusMessageId = focusRequest?.messageId;
  const focusMovesFocus = focusRequest?.focus === true;
  useLayoutEffect(() => {
    if (focusRequestId === undefined || focusMessageId === undefined) return;
    if (
      !revealRow(focusMessageId, { highlight: true }) ||
      !focusMovesFocus ||
      focusedRequestRef.current === focusRequestId
    ) {
      return;
    }
    // PB-5447 — Review lands keyboard focus on the card itself, never on a
    // decision button, and never scrolls anything but the thread. Once per
    // request: a remount replays the reveal, not the focus move.
    const card = [
      ...(scrollRef.current?.querySelectorAll<HTMLElement>(
        `[${CHAT_ROW_ATTRIBUTE}]`,
      ) ?? []),
    ]
      .find((item) => item.getAttribute(CHAT_ROW_ATTRIBUTE) === focusMessageId)
      ?.querySelector<HTMLElement>("[data-proposal-card]");
    if (!card) return;
    card.focus({ preventScroll: true });
    focusedRequestRef.current = focusRequestId;
  }, [
    focusMessageId,
    focusMovesFocus,
    focusRequestId,
    focusedRequestRef,
    revealRow,
    scrollRef,
  ]);
  const focusComposer = useCallback(() => {
    const input = composerRef.current;
    if (input && !input.disabled) input.focus({ preventScroll: true });
  }, [composerRef]);

  // Task 7 / Task 17 — a preview activated in the thread: reveal a rendered
  // target, open the folded stretch that holds it, or ask the host to load it.
  const openReference = useCallback(
    (reference: PlayerAIMessageReference) => {
      if (revealRow(reference.messageId, { highlight: true })) return;
      const holder = hiddenEntryHolding(entries, reference.messageId);
      if (holder) {
        flushSync(() =>
          setOpenHidden((current) => new Set(current).add(holder.key)),
        );
        if (revealRow(reference.messageId, { highlight: true })) return;
      }
      onRequestMessage?.(reference);
    },
    [entries, onRequestMessage, revealRow],
  );

  const maybeLoadOlder = useCallback(() => {
    if (!hasMoreHistory || isLoadingHistory) return;
    void onLoadOlderHistory?.();
  }, [hasMoreHistory, isLoadingHistory, onLoadOlderHistory]);

  const onThreadScroll = useCallback(() => {
    onScrollFollow();
    if ((scrollRef.current?.scrollTop ?? 1) <= 24) maybeLoadOlder();
  }, [maybeLoadOlder, onScrollFollow, scrollRef]);

  const renderRow = (m: PlayerAIChatMessage): ReactNode => {
    const bubble = (
      <MessageBubble
        m={m}
        onWidgetAction={handleWidgetAction}
        onMessageFeedback={onMessageFeedback}
        groupConversation={groupConversation}
        onReply={onReply}
        onOpenReference={openReference}
      />
    );
    return (
      <div
        key={m.id}
        {...{ [CHAT_ROW_ATTRIBUTE]: m.id }}
        {...(m.sequence !== undefined
          ? { [CHAT_ROW_SEQUENCE_ATTRIBUTE]: String(m.sequence) }
          : {})}
        className="[&[data-chat-highlight=true]_[data-message-bubble]]:ring-2 [&[data-chat-highlight=true]_[data-message-bubble]]:ring-pb-iris/60 [&[data-chat-highlight=true]_[data-proposal-card]]:ring-2 [&[data-chat-highlight=true]_[data-proposal-card]]:ring-pb-iris/60"
      >
        {m.widget?.kind === "toolProposal" ? (
          <CollapsibleProposal
            payload={m.widget}
            collapsible={
              m.id !== latestProposalId && !proposalNeedsAttention(m.widget)
            }
            scrollRootRef={scrollRef}
          >
            {bubble}
          </CollapsibleProposal>
        ) : (
          bubble
        )}
      </div>
    );
  };

  return (
    <>
      {/* Thread */}
      {/* `relative` makes the thread the containing block for positioned
          content inside it (a screen-reader-only claim is `absolute`), so
          nothing escapes into a surface or the Shell root and makes them
          scrollable (PB-5381). The wrapper holds the jump control over the
          thread's bottom edge without taking any of its space (Task 11). */}
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
        <div
          ref={scrollRef}
          onScroll={onThreadScroll}
          data-playerai-thread
          className="relative flex-1 overflow-y-auto px-4 py-3 min-w-0 [overflow-anchor:none]"
        >
          <div ref={threadContentRef} className="space-y-3">
            {showConversationSkeleton ? (
              <ConversationSkeleton />
            ) : (
              <>
                {(hasMoreHistory || isLoadingHistory) && (
                  <div className="flex justify-center pb-1">
                    <button
                      type="button"
                      onClick={maybeLoadOlder}
                      disabled={!hasMoreHistory || isLoadingHistory}
                      className="h-6 px-2 rounded-md border border-pb-control-border bg-pb-card text-dense-1 text-pb-text-secondary hover:text-pb-text hover:border-pb-iris/40 disabled:opacity-50 disabled:cursor-default transition-colors"
                    >
                      {isLoadingHistory ? "Loading…" : "Load older"}
                    </button>
                  </div>
                )}
                {(showSuggestions || empty) && suggestedPrompts.length > 0 && (
                  <div className="pb-1">
                    <div className="text-dense-2 uppercase tracking-wider font-semibold text-pb-text-muted mb-1.5">
                      Try asking
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {suggestedPrompts.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => void onPickPrompt(p.label)}
                          className="text-start text-xs bg-pb-card border border-pb-control-border hover:border-pb-iris/40 hover:bg-pb-iris-soft/20 text-pb-text-secondary hover:text-pb-text rounded-md px-2 py-1 transition-colors"
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {entries.map((entry) => {
                  if (entry.kind === "row") return renderRow(entry.message);
                  const open =
                    openHidden.has(entry.key) || entry.key === focusGroupKey;
                  return (
                    <div
                      key={entry.key}
                      {...{ [CHAT_ROW_ATTRIBUTE]: entry.key }}
                    >
                      <HiddenExchangeRow
                        humanCount={entry.humanCount}
                        aiCount={entry.aiCount}
                        expanded={open}
                        onToggle={() => toggleHidden(entry.key, open)}
                      >
                        {entry.rows.map(renderRow)}
                      </HiddenExchangeRow>
                    </div>
                  );
                })}
                {thinking && (
                  <div className="flex items-center gap-2 text-pb-text-muted text-xs">
                    <Sparkles className="w-3.5 h-3.5 text-pb-iris-text" />
                    <span className="inline-flex gap-0.5">
                      <Dot delay="0ms" />
                      <Dot delay="120ms" />
                      <Dot delay="240ms" />
                    </span>
                    <span>PlayerAI is thinking…</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {scroll.awayFromBottom ? (
          <ChatJumpControl
            newCount={scroll.newCount}
            onJump={scroll.jumpToLatest}
          />
        ) : null}
      </div>

      <PlayerAIComposer
        groupConversation={groupConversation}
        pastedContext={pastedContext}
        draft={draft}
        setDraft={setDraft}
        onSubmit={onSubmit}
        onKeyDown={onKeyDown}
        composerRef={composerRef}
        composerDisabled={composerDisabled}
        composerReadOnly={composerReadOnly}
        composerPlaceholder={composerPlaceholder}
        // Fixed positions: the reminder coming or going never remounts the
        // host's status (an alert would be read out again).
        composerStatusSlot={
          approvalNotice || composerStatusSlot ? (
            <>
              {approvalNotice ? (
                <ComposerApprovalNotice
                  notice={approvalNotice}
                  onFocusLost={focusComposer}
                />
              ) : null}
              {composerStatusSlot}
            </>
          ) : undefined
        }
        composerMetadataSlot={composerMetadataSlot}
        composerContextAttachments={composerContextAttachments}
        onRemoveContextAttachment={onRemoveContextAttachment}
        composerContextNotice={composerContextNotice}
        composerLeadingSlot={composerLeadingSlot}
        mentions={mentions}
        nextPhaseCommand={nextPhaseCommand}
        slashMenuOpen={slashMenuOpen}
        onSelectNextPhaseCommand={onSelectNextPhaseCommand}
        composerFooterSlot={composerFooterSlot}
        composerHintSlot={composerHintSlot}
        reply={reply}
        typingNames={typingNames}
        runControl={runControl}
        pauseNotice={pauseNotice}
      />
    </>
  );
}

function ConversationSkeleton() {
  return (
    <div role="status" aria-label="Loading conversation" className="space-y-3">
      <span className="sr-only">Loading conversation…</span>
      <div className="flex justify-end">
        <Skeleton className="h-12 w-3/5 rounded-xl" />
      </div>
      <div className="flex justify-start">
        <Skeleton className="h-20 w-4/5 rounded-xl" />
      </div>
      <div className="flex justify-end">
        <Skeleton className="h-10 w-2/5 rounded-xl" />
      </div>
    </div>
  );
}

function Dot({ delay }: { delay: string }) {
  return (
    <span
      className="w-1 h-1 rounded-full bg-pb-iris animate-pulse"
      style={{ animationDelay: delay }}
    />
  );
}

interface HistoryViewProps {
  threads: PlayerAIThread[];
  activeThreadId: string;
  onSelect: (t: PlayerAIThread) => void;
  onNewThread: () => void;
  onDelete?: ((threadId: string) => void) | undefined;
  onClose: () => void;
}

type Bucket = "today" | "yesterday" | "week" | "earlier";

const BUCKET_ORDER: Bucket[] = ["today", "yesterday", "week", "earlier"];
const BUCKET_LABELS: Record<Bucket, string> = {
  today: "Today",
  yesterday: "Yesterday",
  week: "Last 7 days",
  earlier: "Earlier",
};

function bucketFor(ts: number, now: number): Bucket {
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const startToday = startOfToday.getTime();
  const startYesterday = startToday - DAY;
  const startWeek = startToday - 6 * DAY; // last 7 days incl. today
  if (ts >= startToday) return "today";
  if (ts >= startYesterday) return "yesterday";
  if (ts >= startWeek) return "week";
  return "earlier";
}

function lastAssistantPreview(messages: PlayerAIChatMessage[]): string {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (!m) continue;
    if (m.role === "assistant" && m.content) return m.content;
  }
  return "";
}

function HistoryView({
  threads,
  activeThreadId,
  onSelect,
  onNewThread,
  onDelete,
  onClose,
}: HistoryViewProps) {
  const [query, setQuery] = useState("");
  const trimmed = query.trim();
  const q = trimmed.toLowerCase();

  const sorted = useMemo(
    () => [...threads].sort((a, b) => b.updatedAt - a.updatedAt),
    [threads],
  );

  const filtered = useMemo(() => {
    if (!q) return sorted;
    return sorted.filter((t) => {
      if (t.title.toLowerCase().includes(q)) return true;
      return t.messages.some((m) => m.content?.toLowerCase().includes(q));
    });
  }, [sorted, q]);

  // Sampled once at mount (purity: no impure Date.now() during render). The
  // day-buckets are coarse and the panel isn't open across day boundaries, so
  // a per-render "now" was never meaningful.
  const [now] = useState(() => Date.now());
  const grouped = useMemo(() => {
    const map: Record<Bucket, PlayerAIThread[]> = {
      today: [],
      yesterday: [],
      week: [],
      earlier: [],
    };
    for (const t of filtered) {
      map[bucketFor(t.updatedAt, now)].push(t);
    }
    return map;
  }, [filtered, now]);

  const onSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      if (query.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        setQuery("");
      } else {
        e.preventDefault();
        onClose();
      }
    }
  };

  const searchBox = (
    <div className="px-3 pt-3 pb-2 shrink-0 min-w-0">
      <div className="flex items-center gap-1.5 bg-pb-card border border-pb-border rounded-md px-2 py-1.5 focus-within:border-pb-iris/50 transition-colors min-w-0">
        <Search className="w-3.5 h-3.5 text-pb-text-muted shrink-0" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onSearchKeyDown}
          placeholder="Search conversations…"
          aria-label="Search conversations"
          className="flex-1 min-w-0 bg-transparent text-sm text-pb-text placeholder:text-pb-text-muted focus:outline-none"
        />
        {query.length > 0 && (
          <Tooltip content="Clear search" describeChild={false}>
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="w-4 h-4 shrink-0 flex items-center justify-center rounded-full text-pb-text-muted hover:text-pb-text hover:bg-pb-shell"
            >
              <X className="w-3 h-3" />
            </button>
          </Tooltip>
        )}
      </div>
    </div>
  );

  if (sorted.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-10 gap-3 min-w-0">
        <div className="w-10 h-10 rounded-full bg-pb-card border border-pb-border flex items-center justify-center">
          <History className="w-5 h-5 text-pb-text-muted" />
        </div>
        <div className="text-sm font-medium text-pb-text">
          No conversations yet
        </div>
        <div className="text-xs text-pb-text-muted leading-relaxed max-w-60">
          Past conversations will show up here once you've sent a message.
        </div>
        <button
          type="button"
          onClick={onNewThread}
          className="mt-1 inline-flex items-center gap-1 text-xs text-pb-iris-text hover:underline underline-offset-2 font-medium"
        >
          <Plus className="w-3.5 h-3.5" />
          Start a new conversation
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 min-w-0">
      {searchBox}
      <div className="flex-1 overflow-y-auto pb-2 min-w-0">
        {filtered.length === 0 ? (
          <div className="px-4 py-6 text-center flex flex-col items-center gap-2">
            <div className="text-xs text-pb-text-muted leading-relaxed">
              No conversations match “{trimmed}”
            </div>
            <button
              type="button"
              onClick={() => setQuery("")}
              className="text-xs text-pb-iris-text hover:underline underline-offset-2 font-medium"
            >
              Clear search
            </button>
          </div>
        ) : (
          BUCKET_ORDER.map((b) => {
            const items = grouped[b];
            if (items.length === 0) return null;
            return (
              <div key={b} className="mb-1">
                <div className="px-4 pt-3 pb-1 text-dense-2 uppercase tracking-wider font-semibold text-pb-text-muted">
                  {BUCKET_LABELS[b]}
                </div>
                <ul>
                  {items.map((t) => {
                    const isActive = t.id === activeThreadId;
                    const preview = lastAssistantPreview(t.messages);
                    return (
                      <li key={t.id}>
                        <div
                          className={`w-full text-start px-3 py-2.5 transition-colors min-w-0 flex items-start gap-2.5 rounded-md ${
                            isActive
                              ? "bg-pb-iris-soft/30 hover:bg-pb-iris-soft/40"
                              : "hover:pb-overlay-paint"
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => onSelect(t)}
                            className="flex-1 min-w-0 flex items-start gap-2.5 text-start"
                          >
                            {/* IconTile xs circle: the Player AI iris
                                on the active thread (SN-17). */}
                            <IconTile
                              shape="circle"
                              size="xs"
                              tone={isActive ? "ai" : "neutral"}
                              className="mt-0.5 shrink-0"
                            >
                              <MessageSquare />
                            </IconTile>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline gap-2 min-w-0">
                                <div
                                  className={`flex-1 min-w-0 text-sm font-medium truncate leading-snug ${
                                    t.unavailableReason
                                      ? "text-pb-text-muted"
                                      : "text-pb-text"
                                  }`}
                                >
                                  {t.title}
                                </div>
                                <div className="shrink-0 text-dense-1 text-pb-text-muted pb-tabular">
                                  {relativeTime(t.updatedAt)}
                                </div>
                              </div>
                              {/* History only: the app it belongs to is off,
                                  so it opens read-only (module visibility d8). */}
                              {t.unavailableReason && (
                                <div className="mt-0.5 text-xs text-pb-text-muted truncate leading-snug">
                                  {t.unavailableReason}
                                </div>
                              )}
                              {preview && (
                                <div className="mt-0.5 text-xs text-pb-text-muted truncate leading-snug">
                                  {preview}
                                </div>
                              )}
                              {isActive && (
                                <div className="text-dense-2 uppercase tracking-wider text-pb-iris-text font-semibold mt-0.5">
                                  Current
                                </div>
                              )}
                            </div>
                          </button>
                          {onDelete && (
                            <Tooltip
                              content="Delete conversation"
                              describeChild={false}
                              wrapperClassName="shrink-0"
                            >
                              <button
                                type="button"
                                onClick={() => onDelete(t.id)}
                                aria-label={`Delete ${t.title}`}
                                className="w-7 h-7 shrink-0 flex items-center justify-center rounded-md text-pb-text-muted hover:text-pb-red-text hover:pb-overlay-paint transition-colors"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </Tooltip>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/**
 * Hoshin Monitor tab body — surfaces the active strategy context so
 * PlayerAI conversations stay grounded in what the user is looking at.
 * Shows the plan name, selected period, the currently selected X-Matrix
 * item (if any), open decisions grouped by severity, and AI-detected
 * gaps grouped by severity. The "Open Hoshin Monitor" CTA defers to
 * the host page (typically navigates to /app/hoshin).
 */
function HoshinPayloadBody({ payload }: { payload: PlayerAIHoshinPayload }) {
  // Memoized so the `?? []` fallback doesn't mint a new array each render and
  // defeat the useMemo groupings below (exhaustive-deps).
  const decisions = useMemo(() => payload.decisions ?? [], [payload.decisions]);
  const gaps = useMemo(() => payload.gaps ?? [], [payload.gaps]);
  const goals = payload.goals ?? [];
  const targets = payload.targets ?? [];
  const hasItemContext = goals.length > 0 || targets.length > 0;

  const decisionsBySev = useMemo(() => groupBySeverity(decisions), [decisions]);
  // Critical-severity gaps are surfaced FIRST in their own block so
  // operators see them before scanning categories. Everything else is
  // grouped by Hoshin gap category (Alignment / Target / Execution /
  // Ownership / Review / Decision / Data Quality) — which mirrors how
  // the AI Gaps tab and the strategy-health dimensions are organised.
  const criticalGaps = useMemo(
    () => gaps.filter((g) => g.severity === "critical"),
    [gaps],
  );
  const nonCriticalGapsByCategory = useMemo(() => {
    const out: Record<string, PlayerAIHoshinGapRow[]> = {};
    for (const g of gaps) {
      if (g.severity === "critical") continue;
      (out[g.category] ||= []).push(g);
    }
    // Sort each category's rows by severity (high → low).
    for (const cat in out) {
      out[cat]?.sort((a, b) => sevRank(a.severity) - sevRank(b.severity));
    }
    return out;
  }, [gaps]);

  return (
    <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4">
      {/* Header */}
      <div className="rounded-lg border border-pb-border bg-pb-card-elevated px-3 py-2.5">
        <div className="flex items-start gap-2">
          {/* IconTile xs circle in the info tone (SN-17). */}
          <IconTile shape="circle" size="xs" tone="info" className="shrink-0">
            <Compass className="w-3.5 h-3.5" />
          </IconTile>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-pb-text truncate">
              {payload.planName}
            </div>
            <div className="text-dense-1 text-pb-text-muted">
              {payload.period ? `Period · ${payload.period}` : "Hoshin Monitor"}
            </div>
            {payload.selectedItemTitle && (
              <div className="mt-1 text-dense-1 text-pb-text-secondary">
                <span className="text-pb-text-muted">In scope · </span>
                <span className="font-medium text-pb-text">
                  {payload.selectedItemTitle}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {payload.primaryAction && (
        <div className="flex items-stretch gap-1.5">
          <button
            type="button"
            onClick={payload.primaryAction.onClick}
            disabled={payload.primaryAction.disabled}
            className={
              "flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors " +
              (payload.primaryAction.disabled
                ? "bg-pb-card border border-pb-control-border text-pb-text-muted cursor-not-allowed"
                : payload.primaryAction.done
                  ? "bg-pb-green/15 border border-pb-green/50 text-pb-green-text"
                  : "bg-pb-accent border border-pb-accent text-pb-accent-foreground hover:bg-pb-accent-hover active:bg-pb-accent-active shadow-sm")
            }
          >
            {payload.primaryAction.label}
          </button>
          {payload.secondaryAction && (
            <button
              type="button"
              onClick={payload.secondaryAction.onClick}
              disabled={payload.secondaryAction.disabled}
              className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-xs font-semibold bg-pb-card border border-pb-control-border text-pb-text hover:pb-overlay-paint disabled:hover:bg-none disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {payload.secondaryAction.label}
            </button>
          )}
        </div>
      )}
      {payload.primaryAction?.caption && (
        <div className="-mt-2 text-dense-2 text-pb-text-muted">
          {payload.primaryAction.caption}
        </div>
      )}

      {/* Suggested prompt chips — swap based on the active selection
          (gap-specific when a gap is selected, item-specific when a
          matrix item is selected, plan-level otherwise). */}
      {payload.suggestedPrompts && payload.suggestedPrompts.length > 0 && (
        <div>
          <div className="text-dense-2 uppercase tracking-wider text-pb-text-muted font-semibold mb-1.5">
            Suggested prompts
          </div>
          <div className="flex flex-wrap gap-1.5">
            {payload.suggestedPrompts.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => payload.onSelectPrompt?.(p.label)}
                disabled={!payload.onSelectPrompt}
                className="text-dense-1 text-pb-text bg-pb-card border border-pb-control-border rounded-full px-2.5 py-1 hover:pb-overlay-paint hover:border-pb-cyan/40 disabled:hover:bg-none disabled:opacity-60 disabled:cursor-default"
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Goals (Annual Objectives) — simplified Monitor surface. */}
      {goals.length > 0 && (
        <HoshinSection
          icon={
            <Target className="w-3.5 h-3.5 text-pb-cyan-text" aria-hidden />
          }
          title={`Goals (Annual Objectives) · ${goals.length}`}
        >
          <div className="space-y-1">
            {goals.map((it) => (
              <HoshinItemRowChip key={it.id} item={it} />
            ))}
          </div>
        </HoshinSection>
      )}

      {/* True-North Targets. */}
      {targets.length > 0 && (
        <HoshinSection
          icon={
            <Target className="w-3.5 h-3.5 text-pb-teal-text" aria-hidden />
          }
          title={`True-North Targets · ${targets.length}`}
        >
          <div className="space-y-1">
            {targets.map((it) => (
              <HoshinItemRowChip key={it.id} item={it} />
            ))}
          </div>
        </HoshinSection>
      )}

      {/* Decisions — only when the host actually sends them. */}
      {(!hasItemContext || decisions.length > 0) && (
        <HoshinSection
          icon={
            <TriangleAlert
              className="w-3.5 h-3.5 text-pb-amber-text"
              aria-hidden
            />
          }
          title={
            payload.selectedItemTitle
              ? `Decisions affecting "${payload.selectedItemTitle}" · ${decisions.length}`
              : `Decisions needing input · ${decisions.length}`
          }
        >
          {decisions.length === 0 ? (
            <EmptyHoshinNote>
              {payload.selectedItemTitle
                ? "No open decisions touch this item."
                : "No open decisions for this plan right now."}
            </EmptyHoshinNote>
          ) : (
            <div className="space-y-2">
              {(["critical", "high", "medium", "low"] as const).map((sev) => {
                const rows = decisionsBySev[sev];
                if (!rows || rows.length === 0) return null;
                return (
                  <div key={sev} className="space-y-1">
                    <SeverityHeader sev={sev} count={rows.length} />
                    {rows.map((d) => (
                      <DecisionRowChip key={d.id} d={d} />
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </HoshinSection>
      )}

      {/* Gaps — only when the host actually sends them. Critical first,
          then grouped by Hoshin category. */}
      {(!hasItemContext || gaps.length > 0) && (
        <HoshinSection
          icon={
            <Sparkles className="w-3.5 h-3.5 text-pb-iris-text" aria-hidden />
          }
          title={
            payload.selectedItemTitle
              ? `Gaps affecting "${payload.selectedItemTitle}" · ${gaps.length}`
              : `AI-detected gaps · ${gaps.length}`
          }
        >
          {gaps.length === 0 ? (
            <EmptyHoshinNote>
              {payload.selectedItemTitle
                ? "No open gaps touch this item."
                : "No open gaps detected."}
            </EmptyHoshinNote>
          ) : (
            <div className="space-y-3">
              {criticalGaps.length > 0 && (
                <div className="space-y-1">
                  <CategoryHeader
                    label="Critical"
                    count={criticalGaps.length}
                    tone="text-pb-red-text"
                  />
                  {criticalGaps.map((g) => (
                    <GapRowChip
                      key={g.id}
                      g={g}
                      selected={payload.selectedGapId === g.id}
                      {...(payload.onSelectGap !== undefined
                        ? { onSelect: payload.onSelectGap }
                        : {})}
                    />
                  ))}
                </div>
              )}
              {Object.entries(nonCriticalGapsByCategory).map(([cat, rows]) => (
                <div key={cat} className="space-y-1">
                  <CategoryHeader label={cat} count={rows.length} />
                  {rows.map((g) => (
                    <GapRowChip
                      key={g.id}
                      g={g}
                      selected={payload.selectedGapId === g.id}
                      {...(payload.onSelectGap !== undefined
                        ? { onSelect: payload.onSelectGap }
                        : {})}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
        </HoshinSection>
      )}

      {/* CTA */}
      {payload.onOpen && (
        <button
          type="button"
          onClick={payload.onOpen}
          className="w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-md border border-pb-cyan/40 bg-pb-cyan-soft text-pb-cyan-text text-xs font-medium hover:bg-pb-cyan-soft/80"
        >
          <Compass className="w-3.5 h-3.5" /> Open Hoshin Monitor
        </button>
      )}
    </div>
  );
}

function HoshinSection({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-dense-2 uppercase tracking-wider text-pb-text-muted font-semibold mb-1.5">
        {icon}
        <span>{title}</span>
      </div>
      {children}
    </div>
  );
}

function EmptyHoshinNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-dashed border-pb-border bg-pb-card px-2.5 py-2 text-dense-1 text-pb-text-muted">
      {children}
    </div>
  );
}

function SeverityHeader({
  sev,
  count,
}: {
  sev: "critical" | "high" | "medium" | "low";
  count: number;
}) {
  const tone =
    sev === "critical"
      ? "text-pb-red-text"
      : sev === "high"
        ? "text-pb-amber-text"
        : sev === "medium"
          ? "text-pb-cyan-text"
          : "text-pb-text-muted";
  return (
    <div
      className={`text-dense-2 uppercase tracking-wider font-semibold ${tone}`}
    >
      {sev} · {count}
    </div>
  );
}

function CategoryHeader({
  label,
  count,
  tone = "text-pb-text-muted",
}: {
  label: string;
  count: number;
  tone?: string;
}) {
  return (
    <div
      className={`text-dense-2 uppercase tracking-wider font-semibold ${tone}`}
    >
      {label} · {count}
    </div>
  );
}

function GapRowChip({
  g,
  selected,
  onSelect,
}: {
  g: PlayerAIHoshinGapRow;
  selected?: boolean;
  onSelect?: ((gapId: string | null) => void) | undefined;
}) {
  const sevTone =
    g.severity === "critical"
      ? "text-pb-red-text"
      : g.severity === "high"
        ? "text-pb-amber-text"
        : g.severity === "medium"
          ? "text-pb-cyan-text"
          : "text-pb-text-muted";
  const interactive = typeof onSelect === "function";
  const baseCls =
    "block w-full text-start rounded-md border px-2.5 py-1.5 transition-colors";
  /* A chosen gap takes the one selected-card paint, the teal glow on a teal
     edge (AP-06: teal marks the selected state only). */
  const stateCls = selected
    ? `bg-pb-card ${SELECTED}`
    : "border-pb-border bg-pb-card";
  const hoverCls = interactive
    ? " hover:border-pb-cyan/40 hover:pb-overlay-paint cursor-pointer"
    : "";
  const handleClick = interactive
    ? () => onSelect!(selected ? null : g.id)
    : undefined;
  const Tag: "button" | "div" = interactive ? "button" : "div";
  return (
    <Tag
      type={interactive ? "button" : undefined}
      onClick={handleClick}
      className={`${baseCls} ${stateCls}${hoverCls}`}
    >
      <div className="text-xs text-pb-text leading-snug">{g.title}</div>
      <div className="text-dense-2 text-pb-text-muted mt-0.5 flex items-center gap-1.5 flex-wrap">
        <span className={`uppercase font-semibold ${sevTone}`}>
          {g.severity}
        </span>
        <span>· {g.category}</span>
      </div>
      {(g.explanation || g.recommendedAction || g.affectedItems?.length) && (
        <div className="mt-1.5 space-y-1">
          {g.explanation && (
            <div className="text-dense-1 text-pb-text-secondary leading-snug">
              {g.explanation}
            </div>
          )}
          {g.recommendedAction && (
            <div className="text-dense-1 text-pb-text-secondary leading-snug">
              <span className="text-pb-iris-text font-semibold uppercase tracking-wider text-dense-3 me-1">
                Recommended
              </span>
              {g.recommendedAction}
            </div>
          )}
          {g.affectedItems && g.affectedItems.length > 0 && (
            <div className="flex flex-wrap gap-1 pt-0.5">
              {g.affectedItems.slice(0, 4).map((it) => (
                <span
                  key={it.id}
                  className="inline-flex items-center text-dense-2 text-pb-text-muted bg-pb-card-elevated border border-pb-border rounded px-1.5 py-0.5"
                  title={it.id}
                >
                  {it.title}
                </span>
              ))}
              {g.affectedItems.length > 4 && (
                <span className="text-dense-2 text-pb-text-muted">
                  +{g.affectedItems.length - 4}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </Tag>
  );
}

function HoshinItemRowChip({ item }: { item: PlayerAIHoshinItemRow }) {
  const tone =
    item.status === "red"
      ? "bg-pb-red"
      : item.status === "amber"
        ? "bg-pb-amber"
        : item.status === "green"
          ? "bg-pb-green"
          : "bg-pb-text-muted";
  const label =
    item.status === "unknown"
      ? "No status"
      : item.status.charAt(0).toUpperCase() + item.status.slice(1);
  return (
    <div className="rounded-md border border-pb-border bg-pb-card px-2.5 py-1.5">
      <div className="flex items-start gap-2">
        <span
          className={`mt-1 inline-block w-2 h-2 rounded-full shrink-0 ${tone}`}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div className="text-xs text-pb-text leading-snug">{item.title}</div>
          <div className="text-dense-2 text-pb-text-muted mt-0.5 uppercase tracking-wider font-semibold">
            {label}
          </div>
        </div>
      </div>
    </div>
  );
}

function DecisionRowChip({ d }: { d: PlayerAIHoshinDecisionRow }) {
  return (
    <div className="rounded-md border border-pb-border bg-pb-card px-2.5 py-1.5">
      <div className="text-xs text-pb-text leading-snug">{d.title}</div>
      <div className="text-dense-2 text-pb-text-muted mt-0.5">
        Open {d.ageDays}d
      </div>
      {(d.explanation || d.recommendation || d.affectedItems?.length) && (
        <div className="mt-1.5 space-y-1">
          {d.explanation && (
            <div className="text-dense-1 text-pb-text-secondary leading-snug">
              {d.explanation}
            </div>
          )}
          {d.recommendation && (
            <div className="text-dense-1 text-pb-text-secondary leading-snug">
              <span className="text-pb-amber-text font-semibold uppercase tracking-wider text-dense-3 me-1">
                Recommended
              </span>
              {d.recommendation}
            </div>
          )}
          {d.affectedItems && d.affectedItems.length > 0 && (
            <div className="flex flex-wrap gap-1 pt-0.5">
              {d.affectedItems.slice(0, 4).map((it) => (
                <span
                  key={it.id}
                  className="inline-flex items-center text-dense-2 text-pb-text-muted bg-pb-card-elevated border border-pb-border rounded px-1.5 py-0.5"
                  title={it.id}
                >
                  {it.title}
                </span>
              ))}
              {d.affectedItems.length > 4 && (
                <span className="text-dense-2 text-pb-text-muted">
                  +{d.affectedItems.length - 4}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function sevRank(s: "critical" | "high" | "medium" | "low"): number {
  return s === "critical" ? 0 : s === "high" ? 1 : s === "medium" ? 2 : 3;
}

function groupBySeverity<
  T extends PlayerAIHoshinDecisionRow | PlayerAIHoshinGapRow,
>(rows: T[]): Record<"critical" | "high" | "medium" | "low", T[]> {
  const out: Record<"critical" | "high" | "medium" | "low", T[]> = {
    critical: [],
    high: [],
    medium: [],
    low: [],
  };
  for (const r of rows) out[r.severity].push(r);
  return out;
}

function PlaceholderBody({ tab }: { tab: PlayerAITabId }) {
  const Icon = tab === "activity" ? Activity : tab === "hoshin" ? Compass : Zap;
  const description =
    tab === "hoshin"
      ? "Open the Hoshin Monitor to seed PlayerAI with the active strategy: open decisions, AI-detected gaps, and the X-Matrix item in scope."
      : `Coming soon. This panel will surface ${tabLabel(tab).toLowerCase()} in a future update.`;
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-10 gap-3">
      <div
        className={
          "w-10 h-10 rounded-full border flex items-center justify-center " +
          (tab === "hoshin"
            ? "bg-pb-iris-soft border-pb-iris/30 text-pb-iris-text"
            : "bg-pb-card border-pb-border text-pb-text-muted")
        }
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="text-sm font-medium text-pb-text">{tabLabel(tab)}</div>
      <div className="text-xs text-pb-text-muted leading-relaxed max-w-65">
        {description}
      </div>
    </div>
  );
}
